// Buyers + GPP Partners — the two Phase-3 reference objects with their own
// top-level views.

// ============ BUYERS ============
// One row per buyer FIRM, off /api/buyers/roster: the `buyers` ICP + agreement
// profile joined to that firm's deal activity.
//
// This replaced a stub that read /buyers/directory — a regex over
// buyer_buyside engagement *titles*. That predated the Buyer Report import,
// so none of the imported ICP (industries, criteria, size bands) or the
// agreement lifecycle (131) was reachable from the UI, and the page could not
// be filtered at all.
//
// Money is stored in ABSOLUTE dollars (051); the workbook writes $MM and the
// importer multiplies. Never re-scale here.

// Tier: PB roster / PEG blast list / a known buyer on neither (202610071300).
const BUYER_TYPE_LABEL = { preferred_buyer: 'Preferred', peg: 'PEG', other: 'Other' };
const BUYER_TYPE_HUE = { preferred_buyer: 'violet', peg: 'blue', other: 'slate' };
const FIRM_KIND_LABEL = {
  pe_firm: 'PE firm', family_office: 'Family office', independent_sponsor: 'Indep. sponsor',
  search_fund: 'Search fund', holding_company: 'Holding co', pe_backed_platform: 'PE-backed platform',
  strategic: 'Strategic',
};
const FIRM_KIND_HUE = {
  pe_firm: 'blue', family_office: 'violet', independent_sponsor: 'violet', search_fund: 'violet',
  holding_company: 'slate', pe_backed_platform: 'green', strategic: 'green',
};
// ── Touch / iPad helpers ────────────────────────────────────────────────────
// Read window.AtlasDevice at render: a device never changes kind mid-session
// (a desktop window resized narrow stays desktop), and every desktop branch
// below returns exactly what it did before.
const bgTouch = () => !!(window.AtlasDevice && window.AtlasDevice.touch);
const bgTablet = () => !!(window.AtlasDevice && window.AtlasDevice.tablet);
const bgLink = { color: 'var(--accent-text)', textDecoration: 'none' };
const bgStop = (e) => e.stopPropagation();

// Grid cells on touch: the number dials / the address composes; the rest of
// the cell still opens the editor. undefined = EditableCell's own read view.
const bgPhoneView = (raw) => {
  if (!bgTouch() || !raw || !window.AtlasPhone) return undefined;
  const phones = window.AtlasPhone.parsePhones(raw);
  if (!phones.length) return undefined;
  return (
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {phones.map((ph, i) => (
        <React.Fragment key={ph.tel}>
          {i > 0 && ' · '}
          <a href={ph.tel} onClick={bgStop} style={bgLink}>{ph.display}</a>
        </React.Fragment>
      ))}
    </span>
  );
};
const bgEmailView = (email) => {
  const v = (email || '').trim();
  if (!bgTouch() || !v) return undefined;
  return (
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      <a href={`mailto:${v}`} onClick={bgStop} style={bgLink}>{v}</a>
    </span>
  );
};

// iPad: PageHeader (deals.jsx) is one non-wrapping row — title, a sentence
// of meta and 4–8 actions — which runs off a 744–1180px content area. Same
// look, but the actions wrap and the meta drops to its own line. Desktop
// renders PageHeader itself, untouched.
const BgPageHeader = (props) => {
  if (!bgTablet()) return <PageHeader {...props} />;
  const { breadcrumb, title, meta, actions } = props;
  return (
    <header style={{ padding: '12px 16px 10px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', marginBottom: 6 }}>
        {breadcrumb.map((b, i) => (
          <React.Fragment key={i}>
            <span>{b}</span>
            {i < breadcrumb.length - 1 && <Icon.ChevronR size={11} color="var(--text-4)" />}
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 19, fontWeight: 600, color: 'var(--text)' }}>{title}</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0 }}>{actions}</div>
      </div>
      {meta && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6, lineHeight: 1.45 }}>{meta}</div>}
    </header>
  );
};

// iPad: tab strips scroll sideways instead of running off the page.
const bgTabStrip = () => (bgTablet() ? { overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' } : null);
const bgTabBtn = () => (bgTablet() ? { flexShrink: 0, whiteSpace: 'nowrap', minHeight: 40 } : null);

const ROLE_BUCKET_LABEL = {
  head_of_bd: 'Business development', vp: 'Vice president', principal_senior_associate: 'Principal / Sr. associate',
  partner_md: 'Partner / MD', associate: 'Associate', operations_other: 'Operations / other',
};
const AGREEMENT_LABEL  = { pb: 'PBA', umbrella: 'Umbrella' };

// Expiry buckets, derived client-side from the date so a row can't show a
// stale bucket between workbook imports.
const EXPIRY_SOON_DAYS = 90;

// The last time the scraper read this domain it concluded the site is not an
// investment firm. On a roster of PE firms that is usually the wrong DOMAIN on
// the record rather than the wrong firm — "119th Street Capital" carrying
// 119th.com. A suggestion for a human, never acted on automatically: fix the
// domain and the next sweep clears the flag by itself.
const DOMAIN_SUSPECT_HINT =
  'Domain looks wrong — the site we read is not an investment firm. '
  + 'Check the domain points at the right firm.';

const buyerDaysToExpiry = (iso) => {
  if (!iso) return null;
  const ms = new Date(`${iso}T00:00:00`).getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000);
};

const buyerExpiryHue = (days) => {
  if (days === null) return 'slate';
  if (days < 0) return 'red';
  if (days <= EXPIRY_SOON_DAYS) return 'amber';
  return 'green';
};

const fmtBuyerDate = (iso) => (iso
  ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—');

// Absolute dollars in, compact $MM out. A band with neither bound is unknown
// (most of the PEG universe), which reads as "—" rather than "$0".
const fmtBuyerMoney = (v) => {
  if (v === null || v === undefined) return null;
  const mm = v / 1_000_000;
  return `$${mm % 1 === 0 ? mm.toFixed(0) : mm.toFixed(1)}M`;
};

const fmtBuyerBand = (min, max) => {
  const lo = fmtBuyerMoney(min);
  const hi = fmtBuyerMoney(max);
  if (!lo && !hi) return null;
  if (lo && hi) return `${lo} – ${hi}`;
  return lo ? `${lo}+` : `up to ${hi}`;
};

const fmtBuyerAgo = (iso) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days < 31) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const buyerCell = (col, content, opts = {}) => (
  <div key={col.key}
    title={opts.title}
    style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center',
             justifyContent: opts.right ? 'flex-end' : 'flex-start',
             minWidth: 0, fontSize: 12, color: opts.color || 'var(--text-2)',
             borderRight: col.last ? 'none' : '1px solid var(--border)',
             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    className={opts.right ? 'num' : ''}>
    {content}
  </div>
);

const BUYER_COLUMNS = [
  {
    key: 'firm', label: 'Buyer firm', fixed: true, extra: 60,
    accessor: (b) => b.firm || '',
    xget: (b) => b.firm || '',
    xextra: [
      { key: 'company_id', label: 'Atlas company id', get: (b) => b.company_id || '' },
      { key: 'domain',     label: 'Domain',           get: (b) => b.domain || '' },
      { key: 'buyer_type', label: 'Tier',             get: (b) => b.buyer_type || '' },
    ],
    render: (b, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '6px 10px', minWidth: 0,
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  borderRight: col.last ? 'none' : '1px solid var(--border)' }}>
        <Avatar name={b.firm} size={20} color="oklch(0.50 0.13 295)" />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {b.firm}
          </div>
          <div style={{ fontSize: 11, color: b.domain_suspect ? 'var(--amber-fg)' : 'var(--text-3)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
               title={b.domain_suspect ? DOMAIN_SUSPECT_HINT : undefined}>
            {b.domain_suspect && '\u26a0 '}{b.domain || '—'}
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'tier', label: 'Tier', extra: 20,
    accessor: () => 'Preferred',
    xget: (b) => BUYER_TYPE_LABEL[b.buyer_type] || b.buyer_type || '',
    render: (b, ctx, col) => buyerCell(col, (
      <Pill hue={BUYER_TYPE_HUE[b.buyer_type] || 'slate'} sz="sm">
        {BUYER_TYPE_LABEL[b.buyer_type] || b.buyer_type}
      </Pill>
    )),
  },
  {
    key: 'agreement', label: 'Agreement', extra: 24,
    accessor: () => 'Umbrella · Expired',
    xget: (b) => AGREEMENT_LABEL[b.agreement_kind] || '',
    xextra: [
      { key: 'pb_blast_status', label: 'PB Blast', get: (b) => b.pb_blast_status || '' },
    ],
    render: (b, ctx, col) => buyerCell(col, (
      b.agreement_kind ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Pill hue={b.agreement_kind === 'pb' ? 'teal' : 'slate'} sz="sm">
            {AGREEMENT_LABEL[b.agreement_kind]}
          </Pill>
          {/* Verbatim from the sheet's "PB Blast" cell — Yes means live on the
              broadcast, Expired means dropped from it. */}
          {b.pb_blast_status && (
            <span style={{ fontSize: 11, color: b.pb_blast_status === 'Yes' ? 'var(--text-2)' : 'var(--text-4)' }}>
              {b.pb_blast_status === 'Yes' ? 'on blast' : b.pb_blast_status.toLowerCase()}
            </span>
          )}
        </span>
      ) : <span style={{ color: 'var(--text-4)' }}>—</span>
    ), { title: b.roster_status_note || undefined }),
  },
  {
    key: 'expires', label: 'Expires', extra: 76,
    accessor: () => 'Jan 15, 2028',
    xget: (b) => b.pba_expiration_date || '',
    render: (b, ctx, col) => {
      const days = buyerDaysToExpiry(b.pba_expiration_date);
      if (!b.pba_expiration_date) {
        return buyerCell(col, <span style={{ color: 'var(--text-4)' }}>—</span>);
      }
      // The countdown pill only earns its place when the date needs acting on:
      // already expired, or inside the renewal window. On an agreement with
      // years left it is noise ("1028d") and the date already says it.
      const urgent = days < 0 || days <= EXPIRY_SOON_DAYS;
      return buyerCell(col, (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                         color: days < 0 ? 'var(--text-3)' : 'var(--text-2)' }}>
            {fmtBuyerDate(b.pba_expiration_date)}
          </span>
          {urgent && (
            <Pill hue={buyerExpiryHue(days)} sz="sm">
              {days < 0 ? 'expired' : `${days}d`}
            </Pill>
          )}
        </span>
      ), { title: b.roster_status_note || undefined });
    },
  },
  {
    key: 'owner', label: 'Relationship owner', extra: 24,
    accessor: (b) => b.relationship_owner || '',
    xget: (b) => b.relationship_owner || '',
    render: (b, ctx, col) => buyerCell(col, (
      b.relationship_owner ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.relationship_owner}</span>
          {/* An agreement whose owner has left — nobody is chasing the
              renewal. Worth surfacing, not hiding. */}
          {b.relationship_owner_active === false && <Pill hue="red" sz="sm">left</Pill>}
        </span>
      ) : <span style={{ color: 'var(--text-4)' }}>—</span>
    )),
  },
  {
    key: 'ebitda', label: 'EBITDA band', align: 'right', extra: 20,
    accessor: () => '$10.0M – $50.0M',
    xget: (b) => fmtBuyerBand(b.min_ebitda, b.max_ebitda) || '',
    render: (b, ctx, col) => {
      const band = fmtBuyerBand(b.min_ebitda, b.max_ebitda);
      return buyerCell(col, band || <span style={{ color: 'var(--text-4)' }}>—</span>, { right: true });
    },
  },
  {
    key: 'revenue', label: 'Revenue band', align: 'right', extra: 20, def: false,
    accessor: () => '$10.0M – $500.0M',
    xget: (b) => fmtBuyerBand(b.min_revenue, b.max_revenue) || '',
    render: (b, ctx, col) => {
      const band = fmtBuyerBand(b.min_revenue, b.max_revenue);
      return buyerCell(col, band || <span style={{ color: 'var(--text-4)' }}>—</span>, { right: true });
    },
  },
  {
    key: 'industries', label: 'Industries of interest', extra: 40,
    accessor: () => 'Accounting & Tax Services, Insurance',
    xget: (b) => (b.preferred_industries || []).join('; '),
    render: (b, ctx, col) => {
      const inds = b.preferred_industries || [];
      if (!inds.length) {
        return buyerCell(col, <span style={{ color: 'var(--text-4)' }}>— no ICP</span>);
      }
      return buyerCell(col, (
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{inds.slice(0, 3).join(', ')}</span>
          {inds.length > 3 && <Pill hue="slate" sz="sm">+{inds.length - 3}</Pill>}
        </span>
      ), { title: inds.join('\n') });
    },
  },
  {
    key: 'criteria', label: 'Investment criteria', extra: 40, def: false,
    accessor: () => 'Sector Focus: People-first services',
    xget: (b) => b.investment_criteria || '',
    render: (b, ctx, col) => buyerCell(col,
      b.investment_criteria || <span style={{ color: 'var(--text-4)' }}>—</span>,
      { title: b.investment_criteria || undefined }),
  },
  {
    // What they OWN, not what they say they buy (144). A portfolio of 40
    // names is not a cell, so this is the count and a way in — the list
    // itself lives in the drawer.
    key: 'portfolio', label: 'Portfolio', align: 'right', extra: 14,
    accessor: () => '999',
    xget: (b) => b.portfolio_count ?? '',
    xextra: [
      { key: 'portfolio_realized', label: 'Portfolio (realized)', get: (b) => b.portfolio_realized ?? '' },
    ],
    render: (b, ctx, col) => {
      const n = b.portfolio_count || 0;
      if (!n) {
        // Zero is genuinely ambiguous from the grid alone — never read, no
        // portfolio page, unreadable page, or a firm that publishes none. The
        // drawer resolves it; the dash just declines to guess.
        return buyerCell(col, <span style={{ color: 'var(--text-4)' }}>—</span>, { right: true });
      }
      const realized = b.portfolio_realized || 0;
      return buyerCell(col, (
        <button
          onClick={(e) => { e.stopPropagation(); ctx.onPortfolio && ctx.onPortfolio(b); }}
          title={`${n} compan${n === 1 ? 'y' : 'ies'} currently listed`
                 + (realized ? `, ${realized} marked realized` : '')
                 + (b.portfolio_last_seen ? ` · last read ${fmtBuyerAgo(b.portfolio_last_seen)}` : '')}
          style={{ appearance: 'none', background: 'none', border: 'none', padding: 0,
                   font: 'inherit', cursor: 'pointer', color: 'var(--accent)', fontWeight: 600 }}>
          {n}
        </button>
      ), { right: true });
    },
  },
  {
    // What KIND of buyer the site reads as (202609221600) — not the PB/PEG
    // tier, which is our relationship with them.
    key: 'firm_kind', label: 'Kind', extra: 22,
    accessor: () => 'Independent sponsor',
    xget: (b) => FIRM_KIND_LABEL[b.firm_kind] || b.firm_kind || '',
    render: (b, ctx, col) => buyerCell(col, (
      b.firm_kind
        ? <Pill hue={FIRM_KIND_HUE[b.firm_kind] || 'slate'} sz="sm">{FIRM_KIND_LABEL[b.firm_kind] || b.firm_kind}</Pill>
        : <span style={{ color: 'var(--text-4)' }}>—</span>
    )),
  },
  {
    // The team page, ranked for outreach. Count is a way in; the list lives
    // in the drawer, with who is / is not in Atlas.
    key: 'team', label: 'Team', align: 'right', extra: 12,
    accessor: () => '99',
    xget: (b) => b.team_count ?? '',
    xextra: [{ key: 'team_in_atlas', label: 'Team in Atlas', get: (b) => b.team_in_atlas ?? '' }],
    render: (b, ctx, col) => {
      const n = b.team_count || 0;
      if (!n) return buyerCell(col, <span style={{ color: 'var(--text-4)' }}>—</span>, { right: true });
      return buyerCell(col, (
        <button onClick={(e) => { e.stopPropagation(); ctx.onTeam && ctx.onTeam(b); }}
          title={`${n} people on their team page, ${b.team_in_atlas || 0} in Atlas`}
          style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                   font: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
          {n}<span style={{ fontWeight: 400, color: 'var(--text-4)' }}>/{b.team_in_atlas || 0}</span>
        </button>
      ), { right: true });
    },
  },
  {
    key: 'fund_size', label: 'Fund size / AUM', extra: 24, def: false,
    accessor: () => '$450M Fund III',
    xget: (b) => b.fund_size_text || '',
    render: (b, ctx, col) => buyerCell(col, b.fund_size_text || <span style={{ color: 'var(--text-4)' }}>—</span>),
  },
  {
    key: 'founded', label: 'Founded', align: 'right', extra: 10, def: false,
    accessor: () => '2004',
    xget: (b) => b.founded_year ?? '',
    render: (b, ctx, col) => buyerCell(col, b.founded_year || <span style={{ color: 'var(--text-4)' }}>—</span>, { right: true }),
  },
  {
    // When the deep read last ran, and how many fields the site did not state.
    key: 'profile_read', label: 'Profile read', extra: 18, def: false,
    accessor: () => '3 days ago · 4 gaps',
    xget: (b) => b.profile_read_at || '',
    xextra: [{ key: 'profile_gaps', label: 'Profile gaps', get: (b) => (b.profile_gaps || []).join('; ') }],
    render: (b, ctx, col) => buyerCell(col, (
      b.profile_read_at
        ? <span title={(b.profile_gaps || []).join(', ') || 'The site states every field'}>
            {fmtBuyerAgo(b.profile_read_at)}
            {(b.profile_gaps || []).length > 0 && <span style={{ color: 'var(--text-4)' }}> · {b.profile_gaps.length} gap{b.profile_gaps.length === 1 ? '' : 's'}</span>}
          </span>
        : <span style={{ color: 'var(--text-4)' }}>never</span>
    )),
  },
  {
    key: 'deals', label: 'Deals', align: 'right', extra: 10,
    accessor: () => '999',
    xget: (b) => b.deals ?? '',
    render: (b, ctx, col) => buyerCell(col, (
      b.deals > 0 ? <span style={{ fontWeight: 600, color: 'var(--text)' }}>{b.deals}</span>
                  : <span style={{ color: 'var(--text-4)' }}>0</span>
    ), { right: true }),
  },
  {
    key: 'active_deals', label: 'Active', align: 'right', extra: 10,
    accessor: () => '999',
    xget: (b) => b.active_deals ?? '',
    render: (b, ctx, col) => buyerCell(col, (
      b.active_deals > 0 ? <Pill hue="green" sz="sm">{b.active_deals}</Pill>
                         : <span style={{ color: 'var(--text-4)' }}>0</span>
    ), { right: true }),
  },
  {
    key: 'nda_signed', label: 'NDAs', align: 'right', extra: 10, def: false,
    accessor: () => '999',
    xget: (b) => b.nda_signed ?? '',
    render: (b, ctx, col) => buyerCell(col, b.nda_signed ?? 0, { right: true }),
  },
  {
    // How many of our deals they said no to, and — once the blast-tracker
    // notes have been read (202609231200) — what they said no to. The count
    // is the way in; the lessons live in the drawer.
    key: 'passed', label: 'Passed', align: 'right', extra: 12, def: false,
    accessor: () => '999',
    xget: (b) => b.passed ?? '',
    xextra: [{ key: 'says_no_to', label: 'Says no to', get: (b) => (b.pass_profile && b.pass_profile.says_no_to) || '' }],
    render: (b, ctx, col) => {
      const p = b.pass_profile || {};
      const n = Math.max(b.passed || 0, p.passes || 0);
      if (!n) return buyerCell(col, <span style={{ color: 'var(--text-4)' }}>0</span>, { right: true });
      if (!p.passes) return buyerCell(col, n, { right: true });
      return buyerCell(col, (
        <button onClick={(e) => { e.stopPropagation(); ctx.onPasses && ctx.onPasses(b); }}
          title={p.says_no_to ? `Says no to: ${p.says_no_to}` : `${p.passes} pass${p.passes === 1 ? '' : 'es'} read, no repeat pattern yet`}
          style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                   font: 'inherit', fontSize: 12, fontWeight: 600, color: p.says_no_to ? 'var(--amber-fg, #b80)' : 'var(--accent)' }}>
          {n}
        </button>
      ), { right: true });
    },
  },
  {
    key: 'last_activity', label: 'Last activity', align: 'right', extra: 16,
    accessor: () => '12mo ago',
    xget: (b) => b.last_next_action || '',
    // The engagement's NextActionDate — the only date humans maintain.
    // last_stage_update/updated_at are churned by every sync pass.
    render: (b, ctx, col) => buyerCell(col, fmtBuyerAgo(b.last_next_action),
      { right: true, color: 'var(--text-3)' }),
  },
  {
    key: 'in_ss', label: 'In SS', extra: 10, def: false,
    accessor: () => 'Unknown',
    xget: (b) => (b.in_sourcescrub === null || b.in_sourcescrub === undefined
      ? '' : (b.in_sourcescrub ? 'Yes' : 'No')),
    // Tri-state: NULL means "not evaluated", never No. The source field has
    // not been identified in DealCloud yet, so today every row reads "—".
    render: (b, ctx, col) => buyerCell(col, (
      b.in_sourcescrub === true ? <Pill hue="green" sz="sm">Yes</Pill>
        : b.in_sourcescrub === false ? <Pill hue="slate" sz="sm">No</Pill>
        : <span style={{ color: 'var(--text-4)' }}>—</span>
    )),
  },
  {
    key: 'icp_source', label: 'ICP source', extra: 16, def: false,
    accessor: () => 'workbook',
    xget: (b) => b.icp_source || '',
    render: (b, ctx, col) => buyerCell(col,
      b.icp_source || <span style={{ color: 'var(--text-4)' }}>—</span>),
  },
  {
    key: 'domain_check', label: 'Domain check', extra: 30, def: false,
    accessor: () => 'Looks wrong',
    xget: (b) => (b.domain_suspect ? 'looks wrong' : (b.site_last_scraped_at ? 'ok' : '')),
    render: (b, ctx, col) => buyerCell(col, (
      b.domain_suspect ? <Pill hue="amber" sz="sm">Looks wrong</Pill>
        : b.site_last_scraped_at ? <Pill hue="slate" sz="sm">Read OK</Pill>
        : <span style={{ color: 'var(--text-4)' }}>Not read</span>
    ), { title: b.site_verdict || undefined }),
  },
  {
    key: 'site_changed', label: 'Site changed', align: 'right', extra: 16, def: false,
    accessor: () => '12mo ago',
    xget: (b) => b.site_last_changed_at || '',
    render: (b, ctx, col) => buyerCell(col, fmtBuyerAgo(b.site_last_changed_at),
      { right: true, color: 'var(--text-3)' }),
  },
  {
    key: 'tail', label: 'Tail (yrs)', align: 'right', extra: 10, def: false,
    accessor: () => '1.5',
    xget: (b) => b.tail_length_years ?? '',
    render: (b, ctx, col) => buyerCell(col,
      b.tail_length_years ?? <span style={{ color: 'var(--text-4)' }}>—</span>, { right: true }),
  },
  {
    key: 'signed', label: 'Signed', extra: 16, def: false,
    accessor: () => 'Jan 15, 2028',
    xget: (b) => b.pba_sign_date || '',
    render: (b, ctx, col) => buyerCell(col, fmtBuyerDate(b.pba_sign_date)),
  },
  {
    key: 'status_note', label: 'Roster note', extra: 40, def: false,
    accessor: () => 'Renewal PB Agreement sent, followed up',
    xget: (b) => b.roster_status_note || '',
    render: (b, ctx, col) => buyerCell(col,
      b.roster_status_note || <span style={{ color: 'var(--text-4)' }}>—</span>,
      { title: b.roster_status_note || undefined }),
  },
];

// Identifier columns appended to every export regardless of layout, so a file
// can always be joined back to Atlas.
const BUYER_EXPORT_ALWAYS = [
  { key: 'company_id', label: 'Atlas company id', get: (b) => b.company_id || '' },
];

const BUYER_SORT_EXTRACT = {
  firm:          (b) => (b.firm || '').toLowerCase(),
  tier:          (b) => (b.buyer_type === 'preferred_buyer' ? 0 : b.buyer_type === 'peg' ? 1 : 2),
  agreement:     (b) => (b.agreement_kind === 'pb' ? 0 : b.agreement_kind === 'umbrella' ? 1 : 2),
  expires:       (b) => b.pba_expiration_date || null,
  signed:        (b) => b.pba_sign_date || null,
  owner:         (b) => (b.relationship_owner || '').toLowerCase(),
  ebitda:        (b) => (b.min_ebitda ?? null),
  revenue:       (b) => (b.min_revenue ?? null),
  industries:    (b) => (b.preferred_industries || []).length,
  criteria:      (b) => (b.investment_criteria || '').toLowerCase(),
  portfolio:     (b) => b.portfolio_count ?? 0,
  deals:         (b) => b.deals ?? 0,
  active_deals:  (b) => b.active_deals ?? 0,
  nda_signed:    (b) => b.nda_signed ?? 0,
  passed:        (b) => b.passed ?? 0,
  last_activity: (b) => b.last_next_action || null,
  site_changed:  (b) => b.site_last_changed_at || null,
  // Suspect first, then read-and-fine, then never read — the order you want
  // when you sort this column to work through it.
  domain_check:  (b) => (b.domain_suspect ? 0 : b.site_last_scraped_at ? 1 : 2),
  tail:          (b) => (b.tail_length_years ?? null),
  in_ss:         (b) => (b.in_sourcescrub === null || b.in_sourcescrub === undefined ? null : (b.in_sourcescrub ? 0 : 1)),
  icp_source:    (b) => b.icp_source || '',
  status_note:   (b) => (b.roster_status_note || '').toLowerCase(),
};

const BuyerRow = React.memo(({ b, cols, cellCtx, onOpen }) => (
  <div
    onClick={() => onOpen && onOpen(b)}
    style={{ display: 'flex', minHeight: 39, alignItems: 'stretch',
             borderBottom: '1px solid var(--border)', cursor: onOpen ? 'pointer' : 'default' }}
    className="row-hover">
    {cols.map((c) => c.render(b, cellCtx, c))}
  </div>
));

// ── What a buyer already OWNS (144) ────────────────────────────────────────
// A stated investment criterion is marketing copy; a portfolio page is a list
// of cheques they actually wrote. Forty names is not a grid cell, so the grid
// carries the count and this drawer carries the list.
//
// The banner at the top is the load-bearing part. An empty list has four
// different meanings and they must not render identically:
//   ok         — we read their page; this is what it says
//   unreadable — we found the page and could not read it (JS card grid, etc.)
//   no_page    — nothing on their site is shaped like a portfolio page
//   never_read — nobody has scraped this firm for one yet
// Only 'ok' with nothing in the list means "this firm publishes no holdings".
const PF_STATUS_HUE = { current: 'green', realized: 'violet', unknown: 'slate' };
const PF_STATUS_LABEL = { current: 'Current', realized: 'Realized', unknown: '' };

const PF_READ_COPY = {
  unreadable: {
    hue: 'amber',
    title: 'We found their portfolio page but could not read it',
    body: 'This is not "they own nothing".',
  },
  no_page: {
    hue: 'slate',
    title: 'No portfolio page on their site',
    body: 'Nothing in their site map is shaped like a portfolio, investments, or companies page.',
  },
  never_read: {
    hue: 'slate',
    title: 'Never scraped for a portfolio',
    body: 'This firm has not been through a portfolio read yet.',
  },
};

const PortfolioRow = ({ c, setCompanyId }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0',
                borderBottom: '1px solid var(--border)',
                opacity: c.dropped_at ? 0.55 : 1 }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {/* Linked to an Atlas company where the identity registry produced a
            strong or exact hit; otherwise the name is just the name, which is
            still the evidence. */}
        {c.company_id ? (
          <button onClick={() => setCompanyId && setCompanyId(c.company_id)}
            title={`Linked to the Atlas record "${c.atlas_company_name}" on ${c.match_reason === 'domain' ? 'website domain' : 'an exact name'}`}
            style={{ appearance: 'none', background: 'none', border: 'none', padding: 0,
                     font: 'inherit', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                     color: 'var(--accent)', textAlign: 'left' }}>
            {c.name}
          </button>
        ) : (
          <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{c.name}</span>
        )}
        {c.status !== 'unknown' && (
          <Pill hue={PF_STATUS_HUE[c.status] || 'slate'} sz="sm">{PF_STATUS_LABEL[c.status]}</Pill>
        )}
        {c.dropped_at && (
          <Pill hue="red" sz="sm" title={`Off their page since ${fmtBuyerAgo(c.dropped_at)}`}>
            off the page
          </Pill>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2,
                    display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {c.sector && <span>{c.sector}</span>}
        {(c.year || c.date_text) && <span>{c.date_text || c.year}</span>}
        {c.website && (
          <a href={c.website} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'var(--text-3)' }}>
            {c.domain || c.website}
          </a>
        )}
      </div>
    </div>
  </div>
);

// ── Team drawer ──────────────────────────────────────────────────────────────
//
// The people on the buyer's team page (202609221600), in the order the house
// rule says to call them: Head of BD, then VP, then Principal / Senior
// Associate (partners lead when the team is just partners). "In Atlas" means
// the name matched a contact at this firm; the others are Clay's list.
// What this firm has said no to — every blast-tracker note read into a
// verdict (202609231200), passes first, with the pattern the matcher uses.
const PASS_CATEGORY_LABEL = {
  size: 'Size', geography: 'Geography', industry_fit: 'Industry fit', business_model: 'Business model',
  customer_concentration: 'Customer concentration', valuation: 'Valuation', timing: 'Timing', conflict: 'Conflict',
  seller_side: 'Seller walked', unclear: 'No reason given',
};
const PASS_OUTCOME_HUE = { passed: 'red', interested: 'green', pending: 'amber', other: 'slate' };
const fmtPassMoney = (v) => (v == null ? '' : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1e3)}k`);

// bare (the v2 Firm workspace's Passes tab): no 'Pattern' box (the v2 header's 'Says no to'
// is its only home), no read-at footer, and a dimmed '—' / skeleton for the empty / loading states.
const BuyerPassesBody = ({ buyer, bare = false }) => {
  const [data, setData] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    setData(null); setFailed(false);
    window.AtlasApi.buyerPasses({ buyer_id: buyer.buyer_id })
      .then((r) => { if (alive) setData(r || null); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [buyer && buyer.buyer_id]);
  const items = (data && data.items) || [];
  const prof = (data && data.profile) || {};
  const passes = items.filter((m) => m.outcome === 'passed');
  const rest = items.filter((m) => m.outcome !== 'passed');
  const Row = ({ m }) => (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <a href={`#/deals/${m.engagement_id}`} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textDecoration: 'none', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}{m.deal}</a>
        <Pill hue={PASS_OUTCOME_HUE[m.outcome] || 'slate'} sz="sm">{m.outcome}</Pill>
        {m.category && <Pill hue="slate" sz="sm">{PASS_CATEGORY_LABEL[m.category] || m.category}{m.size_direction ? ` · ${m.size_direction.replace('_', ' ')}` : ''}</Pill>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
        {[m.seller_industry, m.seller_ebitda != null && `${fmtPassMoney(m.seller_ebitda)} EBITDA`, m.seller_state, m.sent && `sent ${m.sent}`].filter(Boolean).join(' · ')}
      </div>
      {m.summary && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>{m.summary}</div>}
      {!m.summary && m.note && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.5 }}>{m.note}</div>}
    </div>
  );  return (
    <>
        {failed && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Could not load their passes.</div>}
        {!failed && data === null && (bare && window.SkeletonRows ? <window.SkeletonRows rows={3} />
          : <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>)}
        {data !== null && !items.length && (bare ? <div style={{ fontSize: 12, color: 'var(--text-4)' }}>—</div> : (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No blast-tracker notes have been read for this firm yet.</div>
        ))}
        {!bare && prof.says_no_to && (
          <div style={{ marginBottom: 12, border: '1px solid var(--border)', borderRadius: 6, padding: '9px 11px', background: 'var(--surface-2)', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6 }}>
            <b style={{ color: 'var(--text)' }}>Pattern:</b> {prof.says_no_to}
          </div>
        )}
        {passes.length > 0 && (
          <>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
              Passed · {passes.length}{prof.engaged ? ` · engaged on ${prof.engaged}` : ''}
            </div>
            {passes.map((m) => <Row key={m.id} m={m} />)}
          </>
        )}
        {rest.length > 0 && (
          <>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, margin: '16px 0 4px' }}>
              Other notes · {rest.length}
            </div>
            {rest.map((m) => <Row key={m.id} m={m} />)}
          </>
        )}
        {!bare && data && data.read_at && <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 12 }}>Profile rebuilt {fmtBuyerAgo(data.read_at)}</div>}
    </>
  );
};

const BuyerPassesDrawer = ({ buyer, onClose }) => (
  <window.Drawer open={Boolean(buyer)} onClose={onClose} title={buyer ? buyer.firm : ''}
    subtitle="Passed" width={520}>
    <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
      {buyer && <BuyerPassesBody buyer={buyer} />}
    </div>
  </window.Drawer>
);

const BuyerTeamBody = ({ buyer }) => {
  const [data, setData] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    setData(null); setFailed(false);
    window.AtlasApi.buyerTeam({ buyer_id: buyer.buyer_id })
      .then((r) => { if (alive) setData(r || null); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [buyer && buyer.buyer_id]);
  const items = (data && data.items) || [];
  const live = items.filter((m) => !m.dropped_at);
  const gone = items.filter((m) => m.dropped_at);
  const gaps = (data && data.gaps) || [];
  // Apollo (202609281500): "Find contact" asks for a verified work email
  // (one credit on a hit, asked once); "Add to Atlas" creates the contact at
  // the firm and links the row. Both update the row in place.
  const [busy, setBusy] = React.useState(null);
  const patchRow = (id, patch) => setData((d) => (d ? { ...d, items: d.items.map((x) => (x.id === id ? { ...x, ...patch } : x)) } : d));
  const findContact = async (m) => {
    setBusy(`find:${m.id}`);
    try {
      const r = await window.AtlasApi.enrichTeamMember(m.id);
      patchRow(m.id, { email: r.email || m.email, email_status: r.email_status, title: r.title || m.title, linkedin: r.linkedin || m.linkedin,
                       phone: r.phone || m.phone, enriched_at: new Date().toISOString(), enrich_source: 'apollo' });
      pushToast(r.found ? { type: 'success', title: `${m.name}: ${r.email}`, body: r.email_status ? `Apollo says ${r.email_status}` : null }
                        : { type: 'info', title: `Apollo has no email for ${m.name}`, body: r.skipped || 'Asked once; will not be asked again.' });
    } catch (e) { pushToast({ type: 'error', title: 'Could not ask Apollo', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };
  const addToAtlas = async (m) => {
    setBusy(`add:${m.id}`);
    try {
      const r = await window.AtlasApi.addTeamMemberContact(m.id);
      patchRow(m.id, { contact_id: r.contact_id });
      pushToast({ type: 'success', title: r.created ? `${m.name} added to Atlas` : `${m.name} was already in Atlas` });
    } catch (e) { pushToast({ type: 'error', title: 'Could not add the contact', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };
  const Row = ({ m }) => (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)',
                  opacity: m.dropped_at ? 0.55 : 1 }}>
      <div style={{ width: 22, textAlign: 'right', fontSize: 12, color: 'var(--text-4)', paddingTop: 1 }}>
        {m.dropped_at ? '' : `#${m.outreach_rank}`}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {m.contact_id ? (
            <a href={`#/people/${m.contact_id}`} style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--accent)', textDecoration: 'none', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}{m.name}</a>
          ) : (
            <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{m.name}</span>
          )}
          <Pill hue={m.role_bucket === 'head_of_bd' ? 'green' : m.role_bucket === 'vp' || m.role_bucket === 'principal_senior_associate' ? 'blue' : 'slate'} sz="sm">
            {ROLE_BUCKET_LABEL[m.role_bucket] || m.role_bucket}
          </Pill>
          {m.is_primary && <Pill hue="violet" sz="sm">primary contact</Pill>}
          {!m.contact_id && !m.dropped_at && <Pill hue="amber" sz="sm" title="No Atlas contact with this name at the firm">not in Atlas</Pill>}
          {m.email_status === 'verified' && <Pill hue="green" sz="sm" title={`Apollo verified this email${m.enriched_at ? ' · ' + fmtBuyerAgo(m.enriched_at) : ''}`}>verified</Pill>}
          {m.dropped_at && <Pill hue="red" sz="sm" title={`Off the team page since ${fmtBuyerAgo(m.dropped_at)}`}>left the page</Pill>}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2 }}>{m.title}</div>
        {m.why && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.5 }}>{m.why}</div>}
        <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Touch: the address composes and the number dials; the
              switchboard caveat (hover text on desktop) is said inline. */}
          {m.email && (bgTouch()
            ? <a href={`mailto:${m.email}`} style={{ ...bgLink, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}{m.email}</a>
            : <span>{m.email}</span>)}
          {m.phone && (bgTouch()
            ? <a href={`tel:${String(m.phone).replace(/[^\d+]/g, '')}`} style={{ ...bgLink, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}{m.phone} <span style={{ color: 'var(--text-4)' }}>(switchboard)</span></a>
            : <span title="The firm's switchboard, per Apollo">{m.phone}</span>)}
          {m.linkedin && <a href={m.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}LinkedIn</a>}
          {!m.dropped_at && !m.email && !m.enriched_at && window.AtlasApi.enrichTeamMember && (
            <Btn size="xs" variant="ghost" disabled={busy === `find:${m.id}`} onClick={() => findContact(m)}
              title="Ask Apollo for a verified work email">
              {busy === `find:${m.id}` ? 'Asking Apollo…' : 'Find contact'}
            </Btn>
          )}
          {!m.dropped_at && !m.email && m.enriched_at && <span title={`Asked Apollo ${fmtBuyerAgo(m.enriched_at)}`}>no email found</span>}
          {!m.dropped_at && !m.contact_id && window.AtlasApi.addTeamMemberContact && (
            <Btn size="xs" variant="ghost" disabled={busy === `add:${m.id}`} onClick={() => addToAtlas(m)}
              title="Create the Atlas contact at this firm (and push it to DealCloud)">
              {busy === `add:${m.id}` ? 'Adding…' : 'Add to Atlas'}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );  return (
    <>
        {failed && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Could not load this team.</div>}
        {!failed && data === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {data !== null && !items.length && (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {data.read_at ? 'Their site has no readable team page.' : 'This firm has not had a deep profile read yet.'}
          </div>
        )}
        {live.length > 0 && (
          <>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
              On the team page · {live.length} · {live.filter((m) => m.contact_id).length} in Atlas
            </div>
            {live.map((m) => <Row key={m.id} m={m} />)}
          </>
        )}
        {gone.length > 0 && (
          <>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, margin: '16px 0 4px' }}>
              Left the page · {gone.length}
            </div>
            {gone.map((m) => <Row key={m.id} m={m} />)}
          </>
        )}
        {data !== null && (data.firm_kind || data.fund_size_text || data.founded_year || gaps.length > 0) && (
          <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 6, padding: '9px 11px', background: 'var(--surface-2)', fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.6 }}>
            {[data.firm_kind && (FIRM_KIND_LABEL[data.firm_kind] || data.firm_kind), data.fund_size_text,
              data.founded_year && `founded ${data.founded_year}`,
              data.size_band_source === 'inferred' && 'size band inferred from the web'].filter(Boolean).join(' · ')}
            {gaps.length > 0 && (
              <div style={{ marginTop: 4 }}><b style={{ color: 'var(--text-2)' }}>Their site does not state:</b> {gaps.join(', ')}</div>
            )}
            {data.read_at && <div style={{ color: 'var(--text-4)', marginTop: 2 }}>Read {fmtBuyerAgo(data.read_at)}</div>}
          </div>
        )}
    </>
  );
};

const BuyerTeamDrawer = ({ buyer, onClose }) => (
  <window.Drawer open={Boolean(buyer)} onClose={onClose} title={buyer ? buyer.firm : ''}
    subtitle="Team" width={520}>
    <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
      {buyer && <BuyerTeamBody buyer={buyer} />}
    </div>
  </window.Drawer>
);

const BuyerPortfolioBody = ({ buyer, setCompanyId }) => {
  const [data, setData] = React.useState(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setData(null);
    setFailed(false);
    window.AtlasApi.buyerPortfolio({ company_id: buyer.company_id })
      .then((r) => { if (alive) setData(r || null); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [buyer && buyer.company_id]);

  const items = (data && data.items) || [];
  const live = items.filter((c) => !c.dropped_at);
  const gone = items.filter((c) => c.dropped_at);
  const read = (data && data.read) || {};
  const notice = read.status && read.status !== 'ok' ? PF_READ_COPY[read.status] : null;
  return (
    <>
        {failed && (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Could not load this portfolio.</div>
        )}
        {!failed && data === null && (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
        )}

        {data !== null && notice && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '9px 11px',
                        marginBottom: 12, background: 'var(--surface-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <Pill hue={notice.hue} sz="sm">{read.status === 'unreadable' ? 'unread' : read.status.replace('_', ' ')}</Pill>
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{notice.title}</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{notice.body}</div>
            {read.error && (
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>{read.error}</div>
            )}
          </div>
        )}

        {data !== null && !notice && !items.length && (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            We read their portfolio page and it lists no companies.
          </div>
        )}

        {live.length > 0 && (
          <>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                          letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
              Listed today · {live.length}
            </div>
            {live.map((c) => <PortfolioRow key={c.id} c={c} setCompanyId={setCompanyId} />)}
          </>
        )}

        {gone.length > 0 && (
          <>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                          letterSpacing: '0.05em', fontWeight: 500, margin: '16px 0 4px' }}>
              No longer listed · {gone.length}
            </div>
            {/* Kept, not deleted: a company leaving a PE firm's portfolio page
                is an exit, and an exit is a reason to call them. */}
            {gone.map((c) => <PortfolioRow key={c.id} c={c} setCompanyId={setCompanyId} />)}
          </>
        )}

        {data !== null && (items.length > 0 || read.at) && (
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 14 }}>
            {read.at ? `Last read ${fmtBuyerAgo(read.at)}` : 'Never read'}
            {items[0] && items[0].source_url && (
              <>
                {' · '}
                <a href={items[0].source_url} target="_blank" rel="noopener noreferrer"
                  style={{ color: 'var(--text-4)' }}>source page</a>
              </>
            )}
          </div>
        )}
    </>
  );
};

const BuyerPortfolioDrawer = ({ buyer, onClose, setCompanyId }) => (
  <window.Drawer open={Boolean(buyer)} onClose={onClose} title={buyer ? buyer.firm : ''}
    subtitle="Portfolio" width={520}>
    <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
      {buyer && <BuyerPortfolioBody buyer={buyer} setCompanyId={setCompanyId} />}
    </div>
  </window.Drawer>
);


// What moved on buyer websites since we last looked (133). This is a review
// QUEUE, not a log: reviewing a row drops it out of the default feed, so an
// empty list means "nothing new", which is the useful resting state.
const CHANGE_FIELD_LABEL = {
  industry: 'Industry', geography: 'Geography', criteria: 'Criteria',
  ebitda_band: 'EBITDA band', check_size: 'Check size', platform_add_on: 'Platform / add-on',
  // Firecrawl monitor rows (source='monitor') — per page, coarser than the
  // scraper's per-field rows; the scraper re-reads the firm right after.
  portfolio: 'Portfolio', site: 'Site',
};

const CHANGE_HUE = { added: 'green', removed: 'red', changed: 'amber' };

const BuyerChangesPanel = ({ setCompanyId }) => {
  const [items, setItems] = React.useState(null);
  const [coverage, setCoverage] = React.useState(null);
  const [field, setField] = React.useState('');
  const [showReviewed, setShowReviewed] = React.useState(false);
  const [busy, setBusy] = React.useState(null);

  const reload = React.useCallback(() => {
    setItems(null);
    const p = { unreviewed_only: !showReviewed };
    if (field) p.field = field;
    window.AtlasApi.buyerSiteChanges(p)
      .then((r) => { setItems((r && r.items) || []); setCoverage((r && r.coverage) || null); })
      .catch(() => setItems([]));
  }, [field, showReviewed]);
  React.useEffect(() => { reload(); }, [reload]);

  const review = async (id) => {
    setBusy(id);
    try {
      await window.AtlasApi.reviewBuyerSiteChange(id);
      // Drop it from the unreviewed feed immediately rather than refetching —
      // the row is gone either way and a refetch would jump the scroll.
      setItems((prev) => (showReviewed
        ? (prev || []).map((c) => (c.id === id ? { ...c, reviewed_at: new Date().toISOString() } : c))
        : (prev || []).filter((c) => c.id !== id)));
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not mark as reviewed', body: String(e.message || e) });
    } finally {
      setBusy(null);
    }
  };

  const list = items || [];
  const cov = coverage || { buyers_scraped: 0, with_a_reading: 0, last_swept: null, comparable: 0, awaiting_first_read: 0 };
  const backlog = cov.awaiting_first_read || 0;

  return (
    <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                     borderRadius: 8, padding: 10, marginBottom: 14,
                     display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Field:</span>
          <Combobox
            value={field}
            onChange={(e) => setField(e.target.value)}
            options={[{ value: '', label: 'Any field' },
              ...Object.entries(CHANGE_FIELD_LABEL).map(([v, l]) => ({ value: v, label: l }))]}
            style={{ minWidth: 170 }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-3)' }}>
          <input type="checkbox" checked={showReviewed}
            onChange={(e) => setShowReviewed(e.target.checked)} />
          Include reviewed
        </label>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          {items === null ? 'Loading…' : `${list.length.toLocaleString()} change${list.length === 1 ? '' : 's'}`}
        </span>
        <window.HelpTip text="A change is a firm's stated criteria moving between two readings of its site — a new vertical, a different size band, a dropped sector. A newly picked website is not one." />
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
        {items === null && (
          <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
        )}
        {items && !list.length && (
          <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.7 }}>
            {/* An empty feed has two very different meanings. Detecting a change
                needs TWO readings of a site — the first has nothing to compare
                against — so straight after a first sweep the honest answer is
                "nothing to compare yet", not "nothing happened". */}
            {cov.buyers_scraped > 0 ? (
              <>
                <div style={{ color: 'var(--text-2)', fontWeight: 500, marginBottom: 4 }}>
                  {backlog > 0
                    ? 'No changes yet.'
                    : 'Nothing has moved yet — and that is expected.'}
                </div>
                <div>
                  {cov.buyers_scraped.toLocaleString()} buyer site
                  {cov.buyers_scraped === 1 ? '' : 's'} read
                  {cov.last_swept ? ` (last ${fmtBuyerAgo(cov.last_swept)})` : ''},
                  {' '}{cov.with_a_reading.toLocaleString()} with a usable reading of their criteria.
                </div>
                {/* The daily sweep reads never-read sites before it re-reads
                    anything, so while a first-pass backlog exists there is no
                    second reading to be had — say when that ends, or the
                    empty tab reads as broken next to a Slack digest that
                    keeps picking buyer websites. */}
                {backlog > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {backlog.toLocaleString()} buyer site{backlog === 1 ? ' has' : 's have'} never
                    been read.
                  </div>
                )}
              </>
            ) : (
              <>
                No buyer sites have been read yet. Run{' '}
                <code className="mono" style={{ fontSize: 11, padding: '1px 4px', background: 'var(--surface-2)', borderRadius: 3 }}>
                  python scripts/scrape_buyer_sites.py --limit 50
                </code>{' '}
                to start building the baseline.
              </>
            )}
          </div>
        )}
        {list.map((c, i) => (
          <div key={c.id}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
                     borderBottom: i === list.length - 1 ? 'none' : '1px solid var(--border)',
                     opacity: c.reviewed_at ? 0.55 : 1 }}>
            <Pill hue={CHANGE_HUE[c.change_type] || 'slate'} sz="sm">{c.change_type}</Pill>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, color: 'var(--text)', display: 'flex',
                            alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <a href={`#/companies/${c.company_id}`}
                  onClick={(e) => { e.preventDefault(); setCompanyId && setCompanyId(c.company_id); }}
                  style={{ fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>
                  {c.firm}
                </a>
                <span style={{ color: 'var(--text-4)' }}>·</span>
                <span style={{ color: 'var(--text-3)' }}>{CHANGE_FIELD_LABEL[c.field] || c.field}</span>
              </div>
              {/* Removed value struck through, new value plain — the shape of
                  the change should be readable without reading the labels. */}
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>
                {c.old_value && (
                  <span style={{ textDecoration: 'line-through', color: 'var(--text-4)' }}>{c.old_value}</span>
                )}
                {c.old_value && c.new_value && <span style={{ color: 'var(--text-4)' }}> → </span>}
                {c.new_value}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3 }}>
                {fmtBuyerAgo(c.detected_at)}
                {c.source === 'monitor' && ' · via Firecrawl monitor'}
                {c.source_url && (
                  <>
                    {' · '}
                    <a href={c.source_url} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--accent-text)' }}>
                      {c.source_url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}
                    </a>
                  </>
                )}
                {c.reviewed_at && ` · reviewed${c.reviewed_by_name ? ` by ${c.reviewed_by_name}` : ''}`}
              </div>
            </div>
            {!c.reviewed_at && (
              <Btn size="xs" variant="ghost" disabled={busy === c.id} onClick={() => review(c.id)}>
                {busy === c.id ? '…' : 'Reviewed'}
              </Btn>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// "Who should we send this deal to." Lands on a QUEUE, not a search box.
//
// The first version made you look up a deal first, which is the wrong starting
// point: a dealmaker doesn't know which deal to type, they want "what should I
// be running to buyers right now". So the default view is the live deals past
// the D/S call — the window where a deal is actually being taken to buyers —
// most recently moved first, each with its top matches already scored. The
// search is still there to jump to a specific deal.
//
// The score bar is paired with a confidence figure: a buyer with no stated
// band and no stated industry scores NEUTRAL on both, not zero, so a mid
// score can mean "genuinely middling" or "we know almost nothing".
// Display names only. The set of dimensions belongs to buyer_match and is
// expected to grow, so this is a lookup with a fallback, never the list — an
// unrecognised key gets humanised rather than leaking `portfolio_overlap`
// into the UI.
const MATCH_COMPONENT_LABEL = {
  industry: 'Industry', size: 'Size', relationship: 'Relationship', geography: 'Geography',
};

const matchComponentLabel = (k) => MATCH_COMPONENT_LABEL[k]
  || `${k.charAt(0).toUpperCase()}${k.slice(1)}`.replace(/_/g, ' ');

// Post-D/S-call, pre-close dealmaker stages, in pipeline order.
const MATCH_QUEUE_STAGES = [
  { value: 'ds_call',             label: 'Director/Seller Call' },
  { value: 'hot_lead',           label: 'Hot Lead' },
  { value: 'blast',              label: 'PEG/PB Blast' },
  { value: 'bs_call',            label: 'Buyer/Seller Call' },
  { value: 'data_gathering',     label: 'Data Gathering' },
  { value: 'valuation_presented', label: 'Valuation Presented' },
];

const matchHue = (score) => (score >= 0.7 ? 'green' : score >= 0.45 ? 'amber' : 'slate');

// A dimension that landed on exactly 0.5 scored the NEUTRAL default — it was
// neither evidence for nor against. buyer_match's score_buyer() uses the same
// `!= 0.5` test to decide what counts toward `confidence`, so the same
// constant has to decide what we call neutral here or the row contradicts the
// figure it is explaining. Deliberately "neutral" and not "unknown": it is
// almost always unknown, but a component that accumulates (relationship, at
// 1 NDA + 1 deal) can also land on 0.5 by arithmetic, and the label shouldn't
// claim more than the number knows.
const MATCH_NEUTRAL = 0.5;

const matchNeutralDims = (components) => Object.entries(components || {})
  .filter(([, v]) => v === MATCH_NEUTRAL)
  .map(([k]) => matchComponentLabel(k).toLowerCase());

// The figure used to read "60% known", which answered nothing — the first
// question anyone asked of it was "known about what?". So: say what it
// measures, and name the dimensions that fell back to neutral, because that
// is the part that changes how the score should be read. A 76 resting on two
// neutral dimensions is not the same claim as a 76 built on stated facts.
//
// Everything here is derived from the `components` the API returned. It
// deliberately does NOT restate the weights or the roster of dimensions:
// buyer_match owns those, portfolio evidence is being added to the ranking,
// and copy that enumerates today's four would quietly start lying.
const matchConfidenceTitle = (m) => {
  const neutral = matchNeutralDims(m.components);
  const pct = Math.round((m.confidence || 0) * 100);
  const list = neutral.length < 2
    ? (neutral[0] || '')
    : `${neutral.slice(0, -1).join(', ')} and ${neutral[neutral.length - 1]}`;
  return `${pct}% of this score's weight rests on stated evidence.`
    + (neutral.length
      ? ` ${list.charAt(0).toUpperCase()}${list.slice(1)} scored the neutral 50`
        + ' — counted neither for nor against — so '
        + `${neutral.length > 1 ? 'they are' : 'it is'} not in that share.`
      : ' Every dimension had stated data behind it.')
    + ' Open the deal for the per-dimension breakdown.';
};

const fmtMatchMoney = (v) => (v == null ? null : `$${(v / 1e6).toFixed(1)}M`);

// One buyer match: score bar, component breakdown, and the reasoning.
const MatchRow = ({ m, rank, setCompanyId, detailed }) => (
  <div style={{ padding: detailed ? '8px 0' : '5px 0' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 20, fontSize: 10.5, color: 'var(--text-4)', textAlign: 'right' }}
        className="num">{rank}</span>
      <a href={`#/companies/${m.company_id}`}
        onClick={(e) => { e.preventDefault(); setCompanyId && setCompanyId(m.company_id); }}
        style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)', textDecoration: 'none' }}>
        {m.firm}
      </a>
      {m.agreement_kind && (
        <Pill hue={m.agreement_live ? 'teal' : 'slate'} sz="sm">
          {AGREEMENT_LABEL[m.agreement_kind]}{m.agreement_live ? '' : ' expired'}
        </Pill>
      )}
      {m.already_on_deal && <Pill hue="blue" sz="sm">already on deal</Pill>}
      <div style={{ flex: 1 }} />
      <div style={{ width: 70, height: 4, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden' }}>
        {/* HUE, not `var(--${hue}-fg)`: the blue entry maps to --accent-text
            and there is no --blue-fg to resolve. */}
        <div style={{ width: `${Math.round(m.score * 100)}%`, height: '100%',
                      background: (HUE[matchHue(m.score)] || HUE.slate).fg }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', width: 26 }}
        className="num">{Math.round(m.score * 100)}</span>
      <span title={matchConfidenceTitle(m)}
        style={{ fontSize: 10.5, color: 'var(--text-4)', width: 106,
                 whiteSpace: 'nowrap', cursor: 'help' }}>
        {Math.round(m.confidence * 100)}% stated evidence
      </span>
    </div>
    <div style={{ marginLeft: 28, marginTop: 2, fontSize: 11, color: 'var(--text-3)', lineHeight: 1.55 }}>
      {(m.reasons || []).slice(0, detailed ? 99 : 2).join(' · ')}
    </div>
    {detailed && (
      <div style={{ marginLeft: 28, marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {Object.entries(m.components || {}).map(([k, v]) => (
          <span key={k} style={{ fontSize: 10.5, color: 'var(--text-4)' }}>
            {matchComponentLabel(k)}{' '}
            {/* A bare "50" here reads as a mediocre score when it means the
                dimension was never scored at all — say so instead. */}
            {v === MATCH_NEUTRAL
              ? <i style={{ color: 'var(--text-4)' }}>neutral</i>
              : <b style={{ color: v >= 0.7 ? 'var(--text-2)' : 'var(--text-4)' }}>{Math.round(v * 100)}</b>}
          </span>
        ))}
        {/* Shown apart from the four because it is a different kind of number:
            portfolio evidence (144) is not weighted alongside them, it RAISES
            Industry and lifts the total. Its neutral is 0, not 50 — a firm
            with no matching holdings is where it always was, never marked
            down — so it must not be read against MATCH_NEUTRAL. */}
        {m.portfolio_strength > 0 && (
          <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}
            title="Strength of the portfolio evidence: companies this firm already owns in this deal's space. It raises Industry and lifts the score; it never lowers either.">
            Portfolio{' '}
            <b style={{ color: m.portfolio_strength >= 0.7 ? 'var(--text-2)' : 'var(--text-4)' }}>
              +{Math.round(m.portfolio_strength * 100)}
            </b>
          </span>
        )}
      </div>
    )}
  </div>
);

// ── Find buyers for a deal ───────────────────────────────────────────────────
//
// Samuel's buyer search, run by Atlas on one engagement (app/buyer_search):
// read the seller's site → seller profile → rank the roster → model re-rank
// with a reason per buyer → classify PB / PEG / Umbrella / Unaffiliated → who
// to call. Starts (or resumes) a search, polls it, then lets the dealmaker
// tick buyers onto the deal or take the "Input >>>" workbook. Opened from the
// deal page (window.BuyerSearchDrawer).
const SEARCH_STEP_LABEL = {
  reading_site: 'Reading the seller’s website…', analyzing: 'Analyzing the seller…',
  ranking: 'Ranking the roster…', discovering: 'Finding strategic buyers outside Atlas…',
  reranking: 'Judging fit, buyer by buyer…',
  contacts: 'Finding who to call…', writing: 'Writing results…',
};
const CLASS_HUE = { PB: 'violet', PEG: 'blue', Umbrella: 'violet', Unaffiliated: 'slate' };
const FIT_TYPE_LABEL = { platform: 'Platform', add_on: 'Add-on', strategic: 'Strategic', none: 'No fit' };
const FIT_TYPE_HUE = { platform: 'blue', add_on: 'green', strategic: 'amber', none: 'slate' };
const fitDots = (n) => '●'.repeat(n || 0) + '○'.repeat(5 - (n || 0));

const BuyerSearchDrawer = ({ deal, onClose, onAdded }) => {
  const dealId = deal && (deal.id || deal.engagement_id);
  const [search, setSearch] = React.useState(null);   // latest search row (+results when done)
  const [starting, setStarting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [picked, setPicked] = React.useState(() => new Set());
  const [onlyRecommended, setOnlyRecommended] = React.useState(true);
  const [busy, setBusy] = React.useState(null);
  // The seller's "not these" list — carried from the deal's last search.
  const [exclude, setExclude] = React.useState(null);
  const [showExclude, setShowExclude] = React.useState(false);
  // Every source by default, as the deal card's Find buyers runs it.
  const [regional, setRegional] = React.useState(true);
  const [strategic, setStrategic] = React.useState(true);
  // Buyer lists exported from PrivSource / PEinfo / Inven / SourceScrub and
  // imported onto the deal; the next run adds their firms to the pool.
  const [imports, setImports] = React.useState([]);
  const [importSource, setImportSource] = React.useState('privsource');
  const [importing, setImporting] = React.useState(false);
  const importInput = React.useRef(null);
  const loadImports = React.useCallback(() => {
    if (!dealId) return;
    window.AtlasApi.listBuyerListImports(dealId).then((r) => setImports((r && r.items) || [])).catch(() => {});
  }, [dealId]);
  React.useEffect(() => { loadImports(); }, [loadImports]);
  const onImportFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setImporting(true);
    try {
      const r = await window.AtlasApi.importBuyerList(dealId, importSource, f);
      pushToast({ type: 'success', title: `${r.rows} firms imported`, body: `${r.roster} already on the roster · ${r.matched} in Atlas` });
      loadImports();
    } catch (err) {
      pushToast({ type: 'error', title: 'Could not import that list', body: String(err.message || err) });
    } finally { setImporting(false); }
  };
  const removeImport = async (imp) => {
    try { await window.AtlasApi.deleteBuyerListImport(imp.id); loadImports(); }
    catch (err) { pushToast({ type: 'error', title: 'Could not remove', body: String(err.message || err) }); }
  };

  const load = React.useCallback(async (id) => {
    const r = await window.AtlasApi.getBuyerSearch(id);
    setSearch(r);
    setExclude((cur) => (cur === null ? ((r && r.params && r.params.exclude) || []).join('\n') : cur));
    if (r && r.params && r.params.regional) setRegional(true);
    if (r && r.params && r.params.strategic) setStrategic(true);
    return r;
  }, []);

  // On open: resume the latest search for this deal if there is one.
  React.useEffect(() => {
    if (!dealId) return undefined;
    let alive = true;
    window.AtlasApi.listBuyerSearches(dealId)
      .then((r) => { const it = (r && r.items && r.items[0]) || null; if (alive && it) load(it.id); })
      .catch(() => {});
    return () => { alive = false; };
  }, [dealId, load]);

  // Poll while running. A poll that fails (an API deploy switching
  // instances, a network blip) schedules the next one instead of ending the
  // loop — the old form died on its first miss and left "Reading the
  // seller's website…" on screen while the search finished on the server
  // (Andres, Swann Technologies, 2026-09-23).
  const [pollTick, setPollTick] = React.useState(0);
  const [pollError, setPollError] = React.useState(null);
  React.useEffect(() => {
    if (!search || !['queued', 'running'].includes(search.status)) return undefined;
    const t = setTimeout(async () => {
      try { await load(search.id); setPollError(null); }
      catch (e) { setPollError(String(e.message || e)); }
      finally { setPollTick((x) => x + 1); }
    }, pollError ? 5000 : 2500);
    return () => clearTimeout(t);
  }, [search, pollTick, load]);  // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setStarting(true); setError(null); setPicked(new Set());
    try {
      const r = await window.AtlasApi.startBuyerSearch(dealId, { ...(exclude === null ? {} : { exclude }), regional, strategic });
      await load(r.id);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setStarting(false);
    }
  };

  const results = (search && search.results) || [];
  const shown = results.filter((r) => (!onlyRecommended || r.recommend));
  const selectable = shown.filter((r) => r.company_id && !r.already_on_deal);
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pickAll = () => setPicked(new Set(selectable.map((r) => r.id)));

  const addPicked = async () => {
    if (!picked.size) return;
    setBusy('add');
    try {
      const out = await window.AtlasApi.addBuyerSearchResults(search.id, Array.from(picked));
      pushToast({ type: 'success', title: `${out.added} buyer${out.added === 1 ? '' : 's'} added to the deal`,
                  body: out.skipped ? `${out.skipped} already there or not an Atlas company.` : undefined });
      setPicked(new Set());
      await load(search.id);
      onAdded && onAdded();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not add buyers', body: String(e.message || e) });
    } finally {
      setBusy(null);
    }
  };
  const exportXlsx = async () => {
    setBusy('export');
    try { await window.AtlasApi.exportBuyerSearch(search.id); }
    catch (e) { pushToast({ type: 'error', title: 'Export failed', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };

  const p = (search && search.seller_profile) || null;
  const running = search && ['queued', 'running'].includes(search.status);
  const done = search && search.status === 'done';
  const title = deal ? (deal.name || deal.deal_name || 'Deal') : '';

  return (
    <window.Drawer open onClose={onClose} title={`Find buyers · ${title}`}
      width={880} widthKey="buyerSearch">
      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* header / controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Btn size="sm" onClick={start} disabled={starting || Boolean(running)}>
            {starting ? 'Starting…' : running ? 'Running…' : done ? 'Run again' : 'Run buyer search'}
          </Btn>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-3)' }}>
            <input type="checkbox" checked={regional} onChange={(e) => setRegional(e.target.checked)} />
            Regional strategics
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-3)' }}
            title="Searches Apollo for operating companies in the seller's trade and region and reads their sites — slower, and costs a few dollars the first time a region is searched">
            <input type="checkbox" checked={strategic} onChange={(e) => setStrategic(e.target.checked)} />
            Strategic discovery
          </label>
          <select value={importSource} onChange={(e) => setImportSource(e.target.value)}
            style={{ fontSize: 11.5, padding: '2px 4px', border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', color: 'var(--text-2)' }}>
            <option value="privsource">PrivSource</option>
            <option value="peinfo">PEinfo</option>
            <option value="inven">Inven</option>
            <option value="sourcescrub">SourceScrub</option>
            <option value="preqin">Preqin</option>
            <option value="other">Other</option>
          </select>
          <input ref={importInput} type="file" accept=".xlsx,.xlsm,.csv,.tsv" style={{ display: 'none' }} onChange={onImportFile} />
          <Btn size="xs" variant="ghost" onClick={() => importInput.current && importInput.current.click()} disabled={importing}>
            {importing ? 'Importing…' : imports.length ? `Import list (${imports.length})` : 'Import list'}
          </Btn>
          <Btn size="xs" variant="ghost" onClick={() => setShowExclude((v) => !v)}>
            {(() => { const n = (exclude || '').split('\n').filter((x) => x.trim()).length; return n ? `Exclude (${n})` : 'Exclude'; })()}
          </Btn>
          {search && (
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              {running && (SEARCH_STEP_LABEL[search.step] || 'Starting…')}
              {running && pollError && <span style={{ color: 'var(--amber-fg, #b80)', marginLeft: 6 }}>· lost the connection for a moment, retrying…</span>}
              {done && `${search.results_count} buyers judged from ${(search.candidates_considered || 0).toLocaleString()} on the roster · ${fmtBuyerAgo(search.finished_at)}`}
              {done && search.params && search.params.excluded && search.params.excluded.length > 0 && (
                <span title={search.params.excluded.map((x) => `${x.firm} (${x.by})`).join('\n')}> · {search.params.excluded.length} excluded</span>
              )}
              {search.status === 'failed' && <span style={{ color: 'var(--red-fg, #b00)' }}>Failed: {search.error}</span>}
            </span>
          )}
          {error && <span style={{ fontSize: 11.5, color: 'var(--red-fg, #b00)' }}>{error}</span>}
          <div style={{ flex: 1 }} />
          {done && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-3)' }}>
                <input type="checkbox" checked={onlyRecommended} onChange={(e) => setOnlyRecommended(e.target.checked)} />
                Recommended only
              </label>
              <Btn size="xs" variant="ghost" onClick={exportXlsx} disabled={busy === 'export'}
                title="Samuel’s workbook: Input >>>, contacted, roster matches, strategics, regional, consolidated">
                {busy === 'export' ? 'Exporting…' : 'Export Excel'}
              </Btn>
            </>
          )}
        </div>

        {imports.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {imports.map((imp) => (
              <Pill key={imp.id} hue="slate" sz="sm" title={`${imp.file_name || ''} · ${imp.matched_count} in Atlas`}>
                {imp.source} · {imp.row_count}
                <button type="button" onClick={() => removeImport(imp)} title="Remove this list" style={{
                  background: 'transparent', border: 'none', padding: 0, marginLeft: 2,
                  cursor: 'pointer', color: 'inherit', opacity: 0.65, fontSize: 13, lineHeight: 1,
                }}>×</button>
              </Pill>
            ))}
          </div>
        )}

        {showExclude && (
          <textarea value={exclude || ''} onChange={(e) => setExclude(e.target.value)} rows={4}
            placeholder="One firm name or website per line"
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, fontFamily: 'inherit', padding: '6px 8px',
                     border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', resize: 'vertical' }} />
        )}

        {/* seller brief */}
        {p && p.summary && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--surface-2)', fontSize: 12, lineHeight: 1.6 }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 3 }}>
              The seller, as read {p._pages_read && p._pages_read.length ? `from ${p._pages_read.length} page${p._pages_read.length === 1 ? '' : 's'} of its site` : 'from Atlas'}
            </div>
            <div style={{ color: 'var(--text)' }}>{p.summary}</div>
            <div style={{ color: 'var(--text-3)', marginTop: 4 }}>
              {p.customer_type && <span>Customers: <b style={{ color: 'var(--text-2)' }}>{p.customer_type}</b> · </span>}
              {p.footprint && p.footprint.length > 0 && <span>Footprint: {p.footprint.join(', ')} · </span>}
              {p.services && p.services.length > 0 && <span>Services: {p.services.slice(0, 6).join(', ')}</span>}
            </div>
            {p.buyer_thesis && <div style={{ color: 'var(--text-2)', marginTop: 4 }}><b>Buyer thesis:</b> {p.buyer_thesis}</div>}
          </div>
        )}

        {!search && !starting && (
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.7, padding: '8px 2px' }}>
            No search yet for this deal.
            Nothing is added to the deal until you tick buyers below.
          </div>
        )}

        {/* results */}
        {done && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 11.5, color: 'var(--text-3)' }}>
              <span>{shown.length} shown{onlyRecommended ? ` · ${results.length - shown.length} not recommended hidden` : ''}</span>
              <div style={{ flex: 1 }} />
              <button onClick={pickAll} style={{ appearance: 'none', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 11.5, fontFamily: 'inherit' }}>
                Select all addable ({selectable.length})
              </button>
              <Btn size="xs" onClick={addPicked} disabled={!picked.size || busy === 'add'}>
                {busy === 'add' ? 'Adding…' : `Add ${picked.size || ''} to deal`}
              </Btn>
            </div>
            {shown.map((r, i) => (
              <div key={r.id} style={{ display: 'flex', gap: 10, padding: '10px 12px', alignItems: 'flex-start',
                                         borderBottom: i === shown.length - 1 ? 'none' : '1px solid var(--border)',
                                         opacity: r.recommend ? 1 : 0.6 }}>
                <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)}
                  disabled={!r.company_id || r.already_on_deal} style={{ marginTop: 3 }}
                  title={r.already_on_deal ? 'Already on this deal' : !r.company_id ? 'Not an Atlas company yet' : 'Add to the deal'} />
                <div style={{ width: 22, fontSize: 12, color: 'var(--text-4)', paddingTop: 1 }}>#{r.rank}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {r.company_id ? (
                      <a href={`#/companies/${r.company_id}`} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>{r.firm}</a>
                    ) : r.domain ? (
                      <a href={`https://${r.domain}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>{r.firm}</a>
                    ) : <span style={{ fontSize: 12.5, fontWeight: 600 }}>{r.firm}</span>}
                    <span title={`Fit ${r.fit || '?'} of 5`} style={{ fontSize: 11, letterSpacing: 1, color: (r.fit || 0) >= 4 ? 'var(--green-fg, #2a7)' : (r.fit || 0) >= 3 ? 'var(--amber-fg, #b80)' : 'var(--text-4)' }}>{fitDots(r.fit)}</span>
                    <Pill hue={CLASS_HUE[r.classification] || 'slate'} sz="sm">{r.classification}</Pill>
                    {r.fit_type && <Pill hue={FIT_TYPE_HUE[r.fit_type] || 'slate'} sz="sm">{FIT_TYPE_LABEL[r.fit_type] || r.fit_type}</Pill>}
                    {r.firm_kind && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{FIRM_KIND_LABEL[r.firm_kind] || r.firm_kind}</span>}
                    {r.already_on_deal && <Pill hue="slate" sz="sm">on deal</Pill>}
                    {r.source === 'history' && <Pill hue="green" sz="sm">contacted on this deal</Pill>}
                    {r.source === 'regional' && <Pill hue="amber" sz="sm">regional</Pill>}
                    {r.source === 'discovered' && <Pill hue="teal" sz="sm">discovered</Pill>}
                    {r.source === 'imported' && <Pill hue="violet" sz="sm">imported list</Pill>}
                    {(r.deals_sent > 0 || r.nda_signed > 0) && (
                      <span style={{ fontSize: 11, color: 'var(--text-4)' }}>· {r.deals_sent} deal{r.deals_sent === 1 ? '' : 's'} sent{r.nda_signed ? `, ${r.nda_signed} NDA` : ''}{r.passed ? `, ${r.passed} passed` : ''}</span>
                    )}
                  </div>
                  {r.rationale && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>{r.rationale}</div>}
                  {r.risks && r.risks.length > 0 && (
                    <div style={{ fontSize: 11.5, color: 'var(--amber-fg, #b80)', marginTop: 2 }}>{r.risks.join(' · ')}</div>
                  )}
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3 }}>
                    {r.contact_name ? (
                      <>
                        Call{' '}
                        {r.contact_id ? <a href={`#/people/${r.contact_id}`} style={{ color: 'var(--accent-text)', textDecoration: 'none', fontWeight: 500 }}>{r.contact_name}</a>
                                      : <b style={{ color: 'var(--text-2)' }}>{r.contact_name}</b>}
                        {r.contact_title ? ` — ${r.contact_title}` : ''}{r.contact_email ? ` · ${r.contact_email}` : ''}
                        {!r.contact_id && <Pill hue="amber" sz="sm" title="From the team page; no Atlas contact yet"> not in Atlas</Pill>}
                        {r.contact_source === 'atlas_contacts' && <span style={{ color: 'var(--text-4)' }}> · from Atlas contacts by title</span>}
                      </>
                    ) : <span style={{ color: 'var(--text-4)' }}>No contact on file — team page not read yet.</span>}
                  </div>
                </div>
              </div>
            ))}
            {!shown.length && <div style={{ padding: 20, fontSize: 12, color: 'var(--text-3)' }}>Nothing recommended. Untick “Recommended only” to see every buyer judged.</div>}
          </div>
        )}
      </div>
    </window.Drawer>
  );
};

// ── New buyers (discovery) ───────────────────────────────────────────────────
//
// Firms scripts/discover_buyers.py found on the web that are NOT on the
// roster (202609221500). Each row is what the firm's own homepage said about
// it, with where we found it. Accept creates the company (or attaches to the
// Atlas shell it matched) plus a buyers row — Atlas-only, no DealCloud push.
// The batch accept is for "these are all fine": it takes the same per-row
// path, so nothing is skipped.
const CAND_TYPE_LABEL = {
  pe_firm: 'PE firm', family_office: 'Family office', independent_sponsor: 'Independent sponsor',
  search_fund: 'Search fund', holding_company: 'Holding company', pe_backed_platform: 'PE-backed platform',
  strategic: 'Strategic', lender_or_mezz: 'Lender / mezz', advisor_or_broker: 'Advisor / broker',
  operating_company: 'Operating company', other: 'Other',
};
const CAND_TYPE_HUE = {
  pe_firm: 'blue', family_office: 'violet', independent_sponsor: 'violet', search_fund: 'violet',
  holding_company: 'slate', pe_backed_platform: 'green', strategic: 'green',
};
const CAND_CONF_HUE = { high: 'green', medium: 'amber', low: 'slate' };
const fmtBand = (lo, hi) => {
  if (lo == null && hi == null) return null;
  const f = (v) => (v == null ? '?' : v >= 1e6 ? `$${(v / 1e6).toFixed(v % 1e6 ? 1 : 0)}M` : `$${Math.round(v / 1e3)}k`);
  return `${f(lo)} – ${f(hi)}`;
};

const BuyerCandidatesPanel = ({ setCompanyId, onAccepted }) => {
  const [status, setStatus] = React.useState('new');
  const [type, setType] = React.useState('');
  const [conf, setConf] = React.useState('');
  const [atlasOnly, setAtlasOnly] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [items, setItems] = React.useState(null);
  const [meta, setMeta] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [batching, setBatching] = React.useState(false);

  const reload = React.useCallback(() => {
    setItems(null);
    const p = { status };
    if (type) p.buyer_type = type;
    if (conf) p.confidence = conf;
    if (atlasOnly) p.atlas_only = true;
    if (q.trim()) p.q = q.trim();
    window.AtlasApi.buyerCandidates(p)
      .then((r) => { setItems((r && r.items) || []); setMeta(r || null); })
      .catch(() => setItems([]));
  }, [status, type, conf, atlasOnly, q]);
  React.useEffect(() => { const t = setTimeout(reload, q ? 250 : 0); return () => clearTimeout(t); }, [reload, q]);

  const decide = async (row, decision) => {
    setBusy(row.id);
    try {
      const out = await window.AtlasApi.decideBuyerCandidate(row.id, decision);
      setItems((prev) => (status === 'new'
        ? (prev || []).filter((c) => c.id !== row.id)
        : (prev || []).map((c) => (c.id === row.id ? { ...c, status: decision } : c))));
      setMeta((m) => (m && m.counts ? { ...m, counts: { ...m.counts, new: Math.max(0, (m.counts.new || 0) - 1),
                                                          [decision]: (m.counts[decision] || 0) + 1 } } : m));
      if (decision === 'accepted') {
        pushToast({ type: 'success', title: `${row.name || row.domain} added to the roster`,
                    body: out.created_company ? 'New company created (Atlas-only).' : 'Attached to the existing Atlas company.' });
        onAccepted && onAccepted();
      }
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save the decision', body: String(e.message || e) });
    } finally {
      setBusy(null);
    }
  };

  const acceptBatch = async () => {
    const types = type ? [type] : ['pe_firm', 'family_office', 'independent_sponsor', 'search_fund', 'holding_company', 'pe_backed_platform', 'strategic'];
    const label = `${type ? CAND_TYPE_LABEL[type] : 'every buyer type'}, ${conf || 'high'} confidence${atlasOnly ? ', Atlas shells only' : ''}`;
    if (!window.confirm(`Add all queued firms matching (${label}) to the roster? Each becomes a company + buyer row in Atlas (no DealCloud push).`)) return;
    setBatching(true);
    try {
      const out = await window.AtlasApi.acceptBuyerCandidates({ confidence: conf || 'high', buyer_types: types, atlas_only: atlasOnly, limit: 2000 });
      pushToast({ type: 'success', title: `${out.accepted} buyer${out.accepted === 1 ? '' : 's'} added`,
                  body: `${out.companies_created} new compan${out.companies_created === 1 ? 'y' : 'ies'} created; the rest attached to existing Atlas records.` });
      reload();
      onAccepted && onAccepted();
    } catch (e) {
      pushToast({ type: 'error', title: 'Batch accept failed', body: String(e.message || e) });
    } finally {
      setBatching(false);
    }
  };

  const list = items || [];
  const counts = (meta && meta.counts) || {};
  const types = (meta && meta.types) || {};
  const run = meta && meta.last_run;
  return (
    <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                     borderRadius: 8, padding: 10, marginBottom: 14,
                     display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        {[['new', `To review (${counts.new || 0})`], ['accepted', `Accepted (${counts.accepted || 0})`],
          ['rejected', `Rejected (${counts.rejected || 0})`], ['not_buyer', `Not a buyer (${counts.not_buyer || 0})`]].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)}
            style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5,
                     padding: '4px 10px', borderRadius: 999,
                     border: `1px solid ${status === k ? 'var(--accent)' : 'var(--border)'}`,
                     background: status === k ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
                     color: status === k ? 'var(--text)' : 'var(--text-3)', fontWeight: status === k ? 600 : 500 }}>
            {l}
          </button>
        ))}
        <Combobox value={type} onChange={(e) => setType(e.target.value)} style={{ minWidth: 160 }}
          options={[{ value: '', label: 'Any type' },
            ...Object.entries(CAND_TYPE_LABEL).map(([v, l]) => ({ value: v, label: types[v] ? `${l} (${types[v]})` : l }))]} />
        <Combobox value={conf} onChange={(e) => setConf(e.target.value)} style={{ minWidth: 130 }}
          options={[{ value: '', label: 'Any confidence' }, { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' }, { value: 'low', label: 'Low' }]} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-3)' }}>
          <input type="checkbox" checked={atlasOnly} onChange={(e) => setAtlasOnly(e.target.checked)} />
          Already in Atlas
        </label>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, domain, thesis…"
          style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6,
                   background: 'var(--surface)', color: 'var(--text)', minWidth: 200 }} />
        <div style={{ flex: 1 }} />
        {status === 'new' && list.length > 0 && (
          <Btn size="xs" disabled={batching} onClick={acceptBatch}
            title="Accept every queued firm matching the current type and confidence filters">
            {batching ? 'Adding…' : `Accept all ${conf || 'high'}-confidence${type ? ` ${CAND_TYPE_LABEL[type]}s` : ''}`}
          </Btn>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          {items === null ? 'Loading…' : `${list.length.toLocaleString()} firm${list.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {run && (
        <div style={{ fontSize: 11, color: 'var(--text-4)', margin: '-6px 2px 10px' }}>
          Last discovery run {fmtBuyerAgo(run.finished_at || run.started_at)}: {run.candidates_new} new firms.
        </div>
      )}

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
        {items === null && <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {items && !list.length && (
          <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.7 }}>
            {status === 'new' ? (
              <>
                <div style={{ color: 'var(--text-2)', fontWeight: 500, marginBottom: 4 }}>Nothing waiting for review.</div>
                Run{' '}
                <code className="mono" style={{ fontSize: 11, padding: '1px 4px', background: 'var(--surface-2)', borderRadius: 3 }}>
                  python scripts/discover_buyers.py --scope wide
                </code>{' '}
                to search the web for buyers Atlas does not have.
              </>
            ) : `No ${status.replace('_', ' ')} firms${type || conf || q ? ' match these filters' : ''}.`}
          </div>
        )}
        {list.map((c, i) => (
          <div key={c.id}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
                     borderBottom: i === list.length - 1 ? 'none' : '1px solid var(--border)',
                     opacity: c.status === 'rejected' ? 0.55 : 1 }}>
            <Pill hue={CAND_TYPE_HUE[c.buyer_type] || 'slate'} sz="sm">{CAND_TYPE_LABEL[c.buyer_type] || c.buyer_type || '?'}</Pill>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <a href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer"
                  style={{ fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>
                  {c.name || c.domain}
                </a>
                <span style={{ color: 'var(--text-4)', fontSize: 11.5 }}>{c.domain}</span>
                {(c.hq_city || c.hq_state) && (
                  <span style={{ color: 'var(--text-3)' }}>· {[c.hq_city, c.hq_state].filter(Boolean).join(', ')}</span>
                )}
                <Pill hue={CAND_CONF_HUE[c.confidence] || 'slate'} sz="sm">{c.confidence || '?'}</Pill>
                {c.atlas_company_id && (
                  <a href={`#/companies/${c.atlas_company_id}`}
                    onClick={(e) => { e.preventDefault(); setCompanyId && setCompanyId(c.atlas_company_id); }}
                    style={{ textDecoration: 'none' }}
                    title="Atlas already has this firm as a company (no roster row) — accept attaches the roster row to it">
                    <Pill hue="amber" sz="sm">in Atlas{c.atlas_pursuits ? ` · ${c.atlas_pursuits} pursuit${c.atlas_pursuits === 1 ? '' : 's'}` : ''}</Pill>
                  </a>
                )}
                {fmtBand(c.min_ebitda, c.max_ebitda) && (
                  <span style={{ color: 'var(--text-3)', fontSize: 11.5 }}>· EBITDA {fmtBand(c.min_ebitda, c.max_ebitda)}</span>
                )}
                {c.platform_add_on && <span style={{ color: 'var(--text-3)', fontSize: 11.5 }}>· {c.platform_add_on.replace('_', '-')}</span>}
              </div>
              {c.thesis && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>{c.thesis}</div>}
              {c.sectors && c.sectors.length > 0 && (
                <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{c.sectors.slice(0, 8).join(' · ')}{c.sectors.length > 8 ? ' · …' : ''}</div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3 }}>
                found via {(c.found_via || []).map((v) => v.replace('web:', '').replace('web_search:', '').replace('atlas:', 'Atlas ')).join(', ')}
                {(c.evidence_urls || []).slice(0, 2).map((u) => (
                  <React.Fragment key={u}>
                    {' · '}
                    <a href={u} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>
                      {u.replace(/^https?:\/\/(www\.)?/, '').slice(0, 50)}
                    </a>
                  </React.Fragment>
                ))}
                {c.status !== 'new' && ` · ${c.status.replace('_', ' ')}${c.decided_by_name ? ` by ${c.decided_by_name}` : ''}`}
              </div>
            </div>
            {c.status !== 'accepted' && (
              <Btn size="xs" disabled={busy === c.id} onClick={() => decide(c, 'accepted')}
                title="Add to the buyer roster (creates the company if Atlas does not have it)">
                {busy === c.id ? '…' : 'Add to roster'}
              </Btn>
            )}
            {c.status === 'new' && (
              <Btn size="xs" variant="ghost" disabled={busy === c.id} onClick={() => decide(c, 'rejected')}
                title="Not a buyer we want">
                Skip
              </Btn>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


// ── Intent (buyer signals) ───────────────────────────────────────────────────
//
// The events that say a buyer is about to buy or just changed what it buys
// (app/buyer_intent): exits and new holdings off portfolio pages, team
// joins/leaves, criteria edits, acquisitions of Atlas companies, fund closes
// and press from a weekly web search. Each row is routed to the Atlas
// industries it touches; the Monday digest sends each industry owner theirs.
const INTENT_KIND_LABEL = {
  portfolio_exit: 'Exit', portfolio_add: 'New holding', team_join: 'Joined', team_leave: 'Left',
  criteria_change: 'Criteria changed', acquisition: 'Acquired a company', fund_close: 'Fund close', press: 'In the news',
};
const INTENT_KIND_HUE = {
  portfolio_exit: 'red', portfolio_add: 'green', team_join: 'blue', team_leave: 'amber',
  criteria_change: 'violet', acquisition: 'green', fund_close: 'green', press: 'slate',
};
const INTENT_STRENGTH_HUE = { high: 'green', medium: 'amber', low: 'slate' };

const BuyerIntentPanel = ({ setCompanyId }) => {
  const [status, setStatus] = React.useState('new');
  const [kind, setKind] = React.useState('');
  const [industry, setIndustry] = React.useState('');
  const [strength, setStrength] = React.useState('');
  const [days, setDays] = React.useState(60);
  const [q, setQ] = React.useState('');
  const [items, setItems] = React.useState(null);
  const [meta, setMeta] = React.useState(null);
  const [busy, setBusy] = React.useState(null);

  const reload = React.useCallback(() => {
    setItems(null);
    const p = { status, days };
    if (kind) p.kind = kind;
    if (industry) p.industry_id = industry;
    if (strength) p.strength = strength;
    if (q.trim()) p.q = q.trim();
    window.AtlasApi.buyerIntent(p)
      .then((r) => { setItems((r && r.items) || []); setMeta(r || null); })
      .catch(() => setItems([]));
  }, [status, kind, industry, strength, days, q]);
  React.useEffect(() => { const t = setTimeout(reload, q ? 250 : 0); return () => clearTimeout(t); }, [reload, q]);

  const act = async (row, verb) => {
    setBusy(row.id);
    try {
      await window.AtlasApi.actBuyerSignal(row.id, verb);
      setItems((prev) => (status === 'all'
        ? (prev || []).map((s) => (s.id === row.id ? { ...s, status: verb === 'reopen' ? 'new' : verb === 'review' ? 'reviewed' : 'dismissed' } : s))
        : (prev || []).filter((s) => s.id !== row.id)));
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not update the signal', body: String(e.message || e) });
    } finally {
      setBusy(null);
    }
  };

  const list = items || [];
  const byKind = (meta && meta.by_kind) || {};
  const total = Object.values(byKind).reduce((a, b) => a + b, 0);
  const pill = (active) => ({ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5,
    padding: '4px 10px', borderRadius: 999, border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    background: active ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
    color: active ? 'var(--text)' : 'var(--text-3)', fontWeight: active ? 600 : 500 });
  return (
    <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 10,
                     marginBottom: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        {[['new', `New (${total})`], ['reviewed', 'Reviewed'], ['dismissed', 'Dismissed'], ['all', 'All']].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} style={pill(status === k)}>{l}</button>
        ))}
        <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />
        <button onClick={() => setKind('')} style={pill(kind === '')}>Any kind</button>
        {Object.entries(INTENT_KIND_LABEL).map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} style={pill(kind === k)}>{l}{byKind[k] ? ` (${byKind[k]})` : ''}</button>
        ))}
        <Combobox value={industry} onChange={(e) => setIndustry(e.target.value)} style={{ minWidth: 190 }}
          options={[{ value: '', label: 'Any industry' },
            ...((meta && meta.by_industry) || []).map((i) => ({ value: i.id, label: `${i.name} (${i.n})` }))]} />
        <Combobox value={strength} onChange={(e) => setStrength(e.target.value)} style={{ minWidth: 130 }}
          options={[{ value: '', label: 'Any strength' }, { value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' }, { value: 'low', label: 'Low' }]} />
        <Combobox value={String(days)} onChange={(e) => setDays(Number(e.target.value))} style={{ minWidth: 120 }}
          options={[{ value: '7', label: 'Last 7 days' }, { value: '30', label: 'Last 30 days' }, { value: '60', label: 'Last 60 days' }, { value: '180', label: 'Last 6 months' }]} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search firm or event…"
          style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6,
                   background: 'var(--surface)', color: 'var(--text)', minWidth: 180 }} />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          {items === null ? 'Loading…' : `${list.length.toLocaleString()} signal${list.length === 1 ? '' : 's'}`}
        </span>
        <window.HelpTip text="A holding that fell off a portfolio page, a new one on a page already read, a person who joined or left a team page, a criteria edit, an acquisition of an Atlas company, plus fund closes and press from a weekly web search. A firm's first read is a baseline, not a signal." />
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
        {items === null && <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {items && !list.length && (
          <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.7 }}>
            <div style={{ color: 'var(--text-2)', fontWeight: 500, marginBottom: 4 }}>
              {status === 'new' ? 'No new buyer signals in this window.' : `No ${status} signals in this window.`}
            </div>
          </div>
        )}
        {list.map((s, i) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
                                   borderBottom: i === list.length - 1 ? 'none' : '1px solid var(--border)',
                                   opacity: s.status === 'dismissed' ? 0.55 : 1 }}>
            <Pill hue={INTENT_KIND_HUE[s.kind] || 'slate'} sz="sm">{INTENT_KIND_LABEL[s.kind] || s.kind}</Pill>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <a href={`#/companies/${s.company_id}`}
                  onClick={(e) => { e.preventDefault(); setCompanyId && setCompanyId(s.company_id); }}
                  style={{ fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>
                  {s.firm}
                </a>
                {s.agreement_kind && <Pill hue="violet" sz="sm">{AGREEMENT_LABEL[s.agreement_kind] || s.agreement_kind}</Pill>}
                <Pill hue={INTENT_STRENGTH_HUE[s.strength] || 'slate'} sz="sm">{s.strength}</Pill>
                {(s.industries || []).slice(0, 3).map((n) => <Pill key={n} hue="blue" sz="sm">{n}</Pill>)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>{s.title}</div>
              {s.detail && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{s.detail}</div>}
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3 }}>
                {fmtBuyerAgo(s.occurred_at)} · {s.source_table === 'web_search' ? 'web search' : s.source_table.replace(/_/g, ' ')}
                {s.url && (
                  <>
                    {' · '}
                    <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>
                      {s.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}
                    </a>
                  </>
                )}
                {s.notified_at && ' · in a digest'}
                {s.status !== 'new' && ` · ${s.status}${s.reviewed_by_name ? ` by ${s.reviewed_by_name}` : ''}`}
              </div>
            </div>
            {s.status === 'new' ? (
              <>
                <Btn size="xs" variant="ghost" disabled={busy === s.id} onClick={() => act(s, 'review')}>Reviewed</Btn>
                <Btn size="xs" variant="ghost" disabled={busy === s.id} onClick={() => act(s, 'dismiss')} title="Not useful">Dismiss</Btn>
              </>
            ) : (
              <Btn size="xs" variant="ghost" disabled={busy === s.id} onClick={() => act(s, 'reopen')}>Reopen</Btn>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Acquisitions ─────────────────────────────────────────────────────────────
//
// Companies a buyer's portfolio page says it BOUGHT, matched to Atlas company
// records (202609221400). The domain-tier matches (the holding's own website
// == the Atlas company's website) mark the company acquired on their own; the
// exact-name matches wait here. Confirm flips companies.is_acquired — the
// company leaves every prospect list — so a row carrying a live seller deal is
// flagged: that call is never automatic.
const ACQ_STATUS_LABEL = { current: 'owned', exited: 'since sold', unknown: 'listed' };
const ACQ_CONF_HUE = { high: 'green', medium: 'amber', low: 'slate' };

const BuyerAcquisitionsPanel = ({ setCompanyId }) => {
  const [state, setState] = React.useState('pending');
  const [conf, setConf] = React.useState('');
  const [sort, setSort] = React.useState('confidence');
  // Only rows where the acquirer was on our tracker for the target (the fee
  // check, acquisition_intro) — a client-side filter over the loaded list.
  const [introOnly, setIntroOnly] = React.useState(false);
  const [items, setItems] = React.useState(null);
  const [counts, setCounts] = React.useState(null);
  const [busy, setBusy] = React.useState(null);

  const reload = React.useCallback(() => {
    setItems(null);
    const p = { state, sort };
    if (conf) p.confidence = conf;
    window.AtlasApi.buyerAcquisitions(p)
      .then((r) => { setItems((r && r.items) || []); setCounts((r && r.counts) || null); })
      .catch(() => setItems([]));
  }, [state, conf, sort]);
  React.useEffect(() => { reload(); }, [reload]);

  const decide = async (row, decision) => {
    // Confirm exits the company's live deals as "Company sold" — ask first.
    if (decision === 'confirmed' && row.live_deals > 0) {
      const ok = await showConfirm({
        title: 'Mark acquired and exit the deal?',
        body: `${row.company_name} has ${row.live_deals === 1 ? 'a live engagement' : `${row.live_deals} live engagements`}. `
          + `Marking it acquired${row.acquirer_name ? ` by ${row.acquirer_name}` : ''} exits `
          + `${row.live_deals === 1 ? 'it' : 'them'} as "Company sold" and logs a note with the source on the company`
          + `${row.live_deals === 1 ? ' and the deal' : ''}.`,
        confirmLabel: row.live_deals === 1 ? 'Mark acquired & exit deal' : 'Mark acquired & exit deals',
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(row.id);
    try {
      const out = await window.AtlasApi.decideBuyerAcquisition(row.id, decision);
      const exitedN = decision === 'confirmed' ? (out.exited || []).length : 0;
      setItems((prev) => (state === 'pending'
        ? (prev || []).filter((a) => a.id !== row.id)
        : (prev || []).map((a) => (a.id === row.id
          ? { ...a, decision, decided_at: out.decided_at, live_deals: Math.max(0, a.live_deals - exitedN) } : a))));
      setCounts((c) => (c ? { ...c, pending: Math.max(0, (c.pending || 0) - (row.decision ? 0 : 1)),
                               [decision]: (c[decision] || 0) + 1 } : c));
      if (decision === 'confirmed') window.acquisitionToast(row, out);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save the decision', body: String(e.message || e) });
    } finally {
      setBusy(null);
    }
  };

  const all = items || [];
  const introducedN = all.filter((a) => a.introduced).length;
  const list = introOnly ? all.filter((a) => a.introduced) : all;
  const c = counts || { pending: 0, confirmed: 0, rejected: 0 };
  return (
    <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                     borderRadius: 8, padding: 10, marginBottom: 14,
                     display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        {[['pending', `To review (${c.pending})`], ['confirmed', `Confirmed (${c.confirmed})`],
          ['rejected', `Rejected (${c.rejected})`]].map(([k, l]) => (
          <button key={k} onClick={() => setState(k)}
            style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5,
                     padding: '4px 10px', borderRadius: 999,
                     border: `1px solid ${state === k ? 'var(--accent)' : 'var(--border)'}`,
                     background: state === k ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
                     color: state === k ? 'var(--text)' : 'var(--text-3)', fontWeight: state === k ? 600 : 500 }}>
            {l}
          </button>
        ))}
        <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />
        {/* Confidence: high = the holding's own website matched the Atlas
            company's; medium = exact multi-word name only. Reviewers work the
            tiers separately, so they are pills, not a dropdown. */}
        {[['', 'Any confidence'], ['high', `High${c.pending_high != null && state === 'pending' ? ` (${c.pending_high})` : ''}`],
          ['medium', `Medium${c.pending_medium != null && state === 'pending' ? ` (${c.pending_medium})` : ''}`],
          ['low', `Low${c.pending_low != null && state === 'pending' ? ` (${c.pending_low})` : ''}`]].map(([k, l]) => (
          <button key={k || 'any'} onClick={() => setConf(k)}
            style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5,
                     padding: '4px 10px', borderRadius: 999,
                     border: `1px solid ${conf === k ? 'var(--accent)' : 'var(--border)'}`,
                     background: conf === k ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
                     color: conf === k ? 'var(--text)' : 'var(--text-3)', fontWeight: conf === k ? 600 : 500 }}>
            {l}
          </button>
        ))}
        <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />
        <button onClick={() => setIntroOnly((v) => !v)}
          title="The acquirer (or its sponsor) sat on our blast tracker for this company — a fee may be owed"
          style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5,
                   padding: '4px 10px', borderRadius: 999,
                   border: `1px solid ${introOnly ? 'var(--red-fg, #b42318)' : 'var(--border)'}`,
                   background: introOnly ? 'var(--red-soft, var(--surface-2))' : 'var(--surface)',
                   color: introducedN ? 'var(--red-fg, #b42318)' : 'var(--text-3)', fontWeight: introOnly || introducedN ? 600 : 500 }}>
          {items === null ? 'Introduced (…)' : `Introduced (${introducedN})`}
        </button>
        <Combobox value={sort} onChange={(e) => setSort(e.target.value)} style={{ minWidth: 150 }}
          options={[{ value: 'confidence', label: 'Sort: confidence' }, { value: 'newest', label: 'Sort: newest' },
                    { value: 'company', label: 'Sort: company' }, { value: 'buyer', label: 'Sort: buyer' }]} />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          {items === null ? 'Loading…' : `${list.length.toLocaleString()} compan${list.length === 1 ? 'y' : 'ies'}`}
        </span>
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
        {items === null && <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {items && !list.length && (
          <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.7 }}>
            {state === 'pending' ? (
              <>
                <div style={{ color: 'var(--text-2)', fontWeight: 500, marginBottom: 4 }}>Nothing waiting for review.</div>
              </>
            ) : `No ${state} acquisitions yet.`}
          </div>
        )}
        {list.map((a, i) => (
          <div key={a.id}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
                     borderBottom: i === list.length - 1 ? 'none' : '1px solid var(--border)',
                     opacity: a.decision === 'rejected' ? 0.55 : 1 }}>
            <Pill hue={ACQ_CONF_HUE[a.confidence] || 'slate'} sz="sm">{a.confidence}</Pill>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, color: 'var(--text)', display: 'flex',
                            alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <a href={`#/companies/${a.company_id}`}
                  onClick={(e) => { e.preventDefault(); setCompanyId && setCompanyId(a.company_id); }}
                  style={{ fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>
                  {a.company_name}
                </a>
                {(a.hq_city || a.hq_state) && (
                  <span style={{ color: 'var(--text-4)' }}>{[a.hq_city, a.hq_state].filter(Boolean).join(', ')}</span>
                )}
                {/* Who says so: a portfolio page, the ownership check, a web
                    signal, Apollo, a website read… (acquisition_confirm.jsx) */}
                <Pill hue="slate" sz="sm">{(window.ACQ_SOURCE_LABEL || {})[a.source] || a.source}</Pill>
                <span style={{ color: 'var(--text-4)' }}>·</span>
                <span style={{ color: 'var(--text-3)' }}>
                  {ACQ_STATUS_LABEL[a.status] || a.status} by{' '}
                  {a.acquirer_company_id ? (
                    <a href={`#/companies/${a.acquirer_company_id}`}
                      onClick={(e) => { e.preventDefault(); setCompanyId && setCompanyId(a.acquirer_company_id); }}
                      style={{ color: 'var(--text-2)', fontWeight: 500, textDecoration: 'none' }}>
                      {a.acquirer_name}
                    </a>
                  ) : a.acquirer_name}
                  {a.announced_year ? ` (${a.announced_year})` : ''}
                </span>
                {a.live_deals > 0 && (
                  <Pill hue="red" sz="sm">{a.live_deals} live deal{a.live_deals === 1 ? '' : 's'}</Pill>
                )}
                {a.is_acquired && a.decision !== 'confirmed' && <Pill hue="slate" sz="sm">already flagged</Pill>}
                {a.introduced && (a.intro_level === 'won' ? (
                  <Pill hue="green" sz="sm" title={a.intro_line || ''}>Sold through us</Pill>
                ) : (
                  <Pill hue="red" sz="sm" title={a.intro_line || ''}>
                    We introduced them{a.intro_level === 'strong' ? ' · fee check' : ''}
                  </Pill>
                ))}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>{a.evidence}</div>
              {/* Only a tracker hit is written out. People-history alone
                  (intro_level 'contact') is true of most roster acquirers
                  and would be noise on two rows in three. */}
              {a.introduced && a.intro_line && (
                <div style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.5,
                  color: a.intro_level === 'won' ? 'var(--green-fg, #067647)' : 'var(--red-fg, #b42318)' }}>
                  {a.intro_line}
                  {a.intro_engagement_id && (
                    <> · <a href={`#/deals/${a.intro_engagement_id}`} style={{ color: 'var(--accent-text)' }}>open the deal</a></>
                  )}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3 }}>
                {fmtBuyerAgo(a.first_seen_at)}
                {a.source_url && (
                  <>
                    {' · '}
                    <a href={a.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>
                      {a.source_url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}
                    </a>
                  </>
                )}
                {a.holding_website && (
                  <>
                    {' · '}
                    <a href={a.holding_website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>
                      {a.holding_website.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}
                    </a>
                  </>
                )}
                {a.decision && ` · ${a.decision}${a.decided_by_name ? ` by ${a.decided_by_name}` : (a.decision === 'confirmed' ? ' automatically' : '')}`}
              </div>
            </div>
            {a.decision !== 'confirmed' && (
              <Btn size="xs" disabled={busy === a.id} onClick={() => decide(a, 'confirmed')}
                title={a.live_deals > 0
                  ? 'Mark acquired — exits the live deal as Company sold and logs a note'
                  : 'Mark this company acquired — it leaves every prospect list; logs a note'}>
                {busy === a.id ? '…' : 'Mark acquired'}
              </Btn>
            )}
            {!a.decision && (
              <Btn size="xs" variant="ghost" disabled={busy === a.id} onClick={() => decide(a, 'rejected')}
                title="Wrong company, or not an acquisition">
                Not it
              </Btn>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


const BuyerMatchPanel = ({ setCompanyId, setDealId }) => {
  const [queue, setQueue] = React.useState(null);
  const [stage, setStage] = React.useState('');
  const [industryId, setIndustryId] = React.useState('');
  const [dealmakerId, setDealmakerId] = React.useState('');
  const [industries, setIndustries] = React.useState([]);
  const [owners, setOwners] = React.useState([]);
  const [onlyAgreements, setOnlyAgreements] = React.useState(false);
  const [expanded, setExpanded] = React.useState(null);   // engagement_id
  const [detail, setDetail] = React.useState({});          // engagement_id -> full ranking
  const [dealQ, setDealQ] = React.useState('');
  const [hits, setHits] = React.useState([]);

  // Filter options come from the shared lookups everything else in Atlas
  // filters on — a Combobox handed no source renders "No options".
  React.useEffect(() => {
    window.AtlasApi.listIndustries()
      .then((i) => setIndustries(Array.isArray(i) ? i : (i.items || [])))
      .catch(() => setIndustries([]));
    window.AtlasApi.listTeamMembers()
      .then((m) => setOwners((Array.isArray(m) ? m : (m.items || []))
        .filter((u) => u.active !== false)))
      .catch(() => setOwners([]));
  }, []);

  // Every filter here narrows the DEALS, server-side — the ranking underneath
  // each one still runs against the whole buyer universe. They also all land
  // in the endpoint's cache key, which is what makes flipping them safe.
  const reload = React.useCallback(() => {
    setQueue(null); setExpanded(null); setDetail({});
    const p = { top: 3, limit: 25 };
    if (stage) p.stage = stage;
    if (industryId) p.industry_id = industryId;
    if (dealmakerId) p.dealmaker_id = dealmakerId;
    if (onlyAgreements) p.only_agreements = true;
    window.AtlasApi.buyerMatchQueue(p)
      .then((r) => setQueue(r || { items: [] }))
      .catch(() => setQueue({ items: [], count: 0 }));
  }, [stage, industryId, dealmakerId, onlyAgreements]);
  React.useEffect(() => { reload(); }, [reload]);

  // Jump to any deal, including ones outside the queue's stages. /api/search
  // returns {results:[{entity_type,...}]} — the field is entity_type, not
  // type, which is what silently emptied this list in the first version.
  React.useEffect(() => {
    const term = dealQ.trim();
    if (term.length < 2) { setHits([]); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      window.AtlasApi.search(term, 15)
        .then((r) => {
          if (!alive) return;
          const rows = (r && r.results) || [];
          setHits(rows.filter((x) => x.entity_type === 'engagement').slice(0, 8));
        })
        .catch(() => alive && setHits([]));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [dealQ]);

  // Full ranking for one deal, fetched on expand — the queue only carries the
  // top few, and pulling 25 for every row up front would be wasted work.
  const expand = (engagementId) => {
    if (expanded === engagementId) { setExpanded(null); return; }
    setExpanded(engagementId);
    if (detail[engagementId]) return;
    window.AtlasApi.matchBuyersToDeal({
      engagement_id: engagementId, limit: 25, only_agreements: onlyAgreements,
    })
      .then((r) => setDetail((d) => ({ ...d, [engagementId]: (r && r.items) || [] })))
      .catch(() => setDetail((d) => ({ ...d, [engagementId]: [] })));
  };

  const items = (queue && queue.items) || [];

  return (
    <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
      {/* Controls */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                     borderRadius: 8, padding: 10, marginBottom: 14,
                     display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Stage:</span>
          <Combobox
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            options={[{ value: '', label: 'All post-D/S stages' }, ...MATCH_QUEUE_STAGES]}
            style={{ minWidth: 200 }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Industry:</span>
          <Combobox
            value={industryId}
            onChange={(e) => setIndustryId(e.target.value)}
            options={[{ value: '', label: 'Any industry' },
                      ...industries.map((r) => ({ value: r.id, label: r.name }))]}
            style={{ minWidth: 180 }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Dealmaker:</span>
          <Combobox
            value={dealmakerId}
            onChange={(e) => setDealmakerId(e.target.value)}
            options={[{ value: '', label: 'Any dealmaker' },
                      ...owners.map((u) => ({ value: u.id, label: u.full_name || u.email }))]}
            style={{ minWidth: 180 }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-3)' }}>
          <input type="checkbox" checked={onlyAgreements}
            onChange={(e) => setOnlyAgreements(e.target.checked)} />
          Only buyers under an agreement
        </label>
        <div style={{ position: 'relative' }}>
          <input value={dealQ} onChange={(e) => setDealQ(e.target.value)}
            placeholder="…or jump to a specific deal"
            style={{ width: 220, padding: '4px 10px', fontSize: 12, height: 27,
                     border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'inherit' }} />
          {hits.length > 0 && (
            <div style={{ position: 'absolute', top: 31, left: 0, zIndex: 40, width: 340,
                          background: 'var(--surface)', border: '1px solid var(--border)',
                          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.28)', overflow: 'hidden' }}>
              {hits.map((h) => (
                <div key={h.id} className="row-hover"
                  onClick={() => { setDealQ(''); setHits([]); expand(h.id); }}
                  style={{ padding: '7px 12px', fontSize: 12.5, cursor: 'pointer',
                           borderBottom: '1px solid var(--border)' }}>
                  {h.title}
                  {h.subtitle && (
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{h.subtitle}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {(stage || industryId || dealmakerId || onlyAgreements) && (
          <Btn size="xs" variant="ghost"
            onClick={() => {
              setStage(''); setIndustryId(''); setDealmakerId(''); setOnlyAgreements(false);
            }}>
            Clear
          </Btn>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          {queue === null ? 'Ranking…'
            : `${items.length} deal${items.length === 1 ? '' : 's'} · ${(queue.candidates_considered || 0).toLocaleString()} buyers scored`}
        </span>
      </div>

      {/* Caveats first: they change how the numbers should be read. */}
      {queue && (queue.caveats || []).length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                       borderLeft: `3px solid ${HUE.amber.fg}`, borderRadius: 8,
                       padding: '9px 12px', marginBottom: 14, fontSize: 11.5,
                       color: 'var(--text-3)', lineHeight: 1.6 }}>
          {queue.caveats.map((c, i) => <div key={i}>{c}</div>)}
          {queue.skipped_no_industry > 0 && (
            <div>
              {queue.skipped_no_industry.toLocaleString()} deal
              {queue.skipped_no_industry === 1 ? '' : 's'} in these stages have no industry set
              and are not listed — there is nothing to match them on.
            </div>
          )}
        </div>
      )}

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
        {queue === null && (
          <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>
            Ranking every buyer against each live deal…
          </div>
        )}
        {queue && !items.length && (
          <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6 }}>
            No live deals in {stage ? 'this stage' : 'the post-D/S stages'}
            {industryId || dealmakerId ? ' match these filters.' : ' to match right now.'}
          </div>
        )}
        {items.map((d, i) => {
          const open = expanded === d.engagement_id;
          const full = detail[d.engagement_id];
          return (
            <div key={d.engagement_id}
              style={{ borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--border)' }}>
              {/* Deal header */}
              <div onClick={() => expand(d.engagement_id)}
                style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex',
                         alignItems: 'center', gap: 8, flexWrap: 'wrap',
                         background: open ? 'var(--surface-2)' : 'transparent' }}
                className="row-hover">
                <Icon.ChevronR size={11}
                  style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s',
                           color: 'var(--text-4)', flexShrink: 0 }} />
                <a href={`#/deals/${d.engagement_id}`}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); setDealId && setDealId(d.engagement_id); }}
                  style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>
                  {d.company_name || d.deal_name}
                </a>
                <Pill hue="slate" sz="sm">{d.stage_name}</Pill>
                {d.industry && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{d.industry}</span>}
                {d.ebitda != null && (
                  <span style={{ fontSize: 11.5, color: 'var(--text-3)' }} className="num">
                    {fmtMatchMoney(d.ebitda)} EBITDA
                  </span>
                )}
                {d.state && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{d.state}</span>}
                <div style={{ flex: 1 }} />
                {/* A deal with no buyers on it yet is the one this list is for. */}
                {d.buyers_on_deal === 0
                  ? <Pill hue="amber" sz="sm">no buyers yet</Pill>
                  : <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{d.buyers_on_deal} on deal</span>}
                {d.dealmaker && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{d.dealmaker}</span>}
              </div>

              {/* Matches */}
              <div style={{ padding: '2px 14px 10px 20px' }}>
                {(open && full ? full : d.matches || []).map((m, j) => (
                  <MatchRow key={m.buyer_id} m={m} rank={j + 1}
                    setCompanyId={setCompanyId} detailed={open} />
                ))}
                {open && !full && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '4px 0' }}>
                    Loading the full ranking…
                  </div>
                )}
                {!open && (d.matches || []).length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-4)', marginLeft: 28, marginTop: 2 }}>
                    Click the deal for the full ranking and the reasoning behind each score.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BUYER_FILTER_DEFAULTS = {
  buyer_type: '', agreement_kind: '', blast_status: '', agreement_state: '',
  owner_id: '', has_icp: '', has_portfolio: '', in_sourcescrub: '',
  industry: '', ebitda: '', domain_suspect: '',
};

const BuyersView = ({ setCompanyId, setDealId }) => {
  // 'roster' = the firm grid; 'changes' = what moved on their websites (133).
  const [tab, setTab] = React.useState(() => {
    // The weekly intent digest links to #/buyers?tab=intent.
    const m = /[?&]tab=([a-z]+)/.exec(window.location.hash || '');
    return m && ['roster', 'changes', 'candidates', 'acquisitions', 'intent', 'match'].includes(m[1]) ? m[1] : 'roster';
  });
  const [changeCount, setChangeCount] = React.useState(null);
  const [acqCount, setAcqCount] = React.useState(null);
  const [candCount, setCandCount] = React.useState(null);
  const [intentCount, setIntentCount] = React.useState(null);
  const [rows, setRows] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [industryInput, setIndustryInput] = React.useState('');
  const [ebitdaInput, setEbitdaInput] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [filters, setFilters] = React.useState(() => ({ ...BUYER_FILTER_DEFAULTS }));
  // The roster row whose portfolio is open in the drawer, or null.
  const [portfolioOf, setPortfolioOf] = React.useState(null);
  const [teamOf, setTeamOf] = React.useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  // Text/number filters debounce separately from the dropdowns so typing an
  // industry doesn't fire a request per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, industry: industryInput.trim() })), 300);
    return () => clearTimeout(t);
  }, [industryInput]);
  React.useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, ebitda: ebitdaInput.trim() })), 300);
    return () => clearTimeout(t);
  }, [ebitdaInput]);

  React.useEffect(() => {
    // Inactive members are kept: 24 PB agreements are owned by people who
    // have left, and filtering to their name is exactly how you find them.
    window.AtlasApi.listTeamMembers()
      .then((m) => setUsers(Array.isArray(m) ? m : []))
      .catch(() => setUsers([]));
  }, []);

  // Unreviewed site-change count for the tab badge. Fetched once — the badge
  // is a nudge, not a live counter, and the Changes tab refetches on open.
  React.useEffect(() => {
    window.AtlasApi.buyerSiteChanges({ unreviewed_only: true, limit: 500 })
      .then((r) => setChangeCount(((r && r.items) || []).length))
      .catch(() => setChangeCount(null));
    window.AtlasApi.buyerAcquisitions({ state: 'pending', limit: 1 })
      .then((r) => setAcqCount((r && r.counts && r.counts.pending) || 0))
      .catch(() => setAcqCount(null));
    window.AtlasApi.buyerCandidates({ status: 'new', limit: 1 })
      .then((r) => setCandCount((r && r.counts && r.counts.new) || 0))
      .catch(() => setCandCount(null));
    window.AtlasApi.buyerIntent({ status: 'new', limit: 1, days: 30 })
      .then((r) => setIntentCount(Object.values((r && r.by_kind) || {}).reduce((a, b) => a + b, 0)))
      .catch(() => setIntentCount(null));
  }, []);

  const reload = React.useCallback(() => {
    setRows(null);
    const p = {};
    if (debouncedQ) p.q = debouncedQ;
    if (filters.buyer_type)      p.buyer_type      = filters.buyer_type;
    if (filters.agreement_kind)  p.agreement_kind  = filters.agreement_kind;
    if (filters.blast_status)    p.blast_status    = filters.blast_status;
    if (filters.agreement_state) p.agreement_state = filters.agreement_state;
    if (filters.owner_id === '__departed') p.owner_departed = true;
    else if (filters.owner_id)   p.owner_id        = filters.owner_id;
    if (filters.has_icp)         p.has_icp         = filters.has_icp === 'yes';
    if (filters.has_portfolio)   p.has_portfolio   = filters.has_portfolio === 'yes';
    if (filters.in_sourcescrub)  p.in_sourcescrub  = filters.in_sourcescrub;
    if (filters.domain_suspect)  p.domain_suspect  = filters.domain_suspect === 'yes';
    if (filters.industry)        p.industry        = filters.industry;
    // Absolute dollars, both here and on the endpoint (051). The box used to
    // ask for $MM and multiply by 1e6, so a bare "4" meant $4M; it now takes
    // the same shorthand as every other money field ("4M", "100k",
    // "4000000") and is read literally, with a live hint next to it spelling
    // out the figure. Unparseable text applies no EBITDA filter at all.
    const ebitda = window.parseNumInput(filters.ebitda);
    if (typeof ebitda === 'number') p.ebitda = ebitda;
    window.AtlasApi.buyerRoster(p)
      .then((r) => setRows((r && r.items) || []))
      .catch(() => setRows([]));
  }, [debouncedQ, filters]);
  React.useEffect(() => { reload(); }, [reload]);

  const list = rows || [];
  const empty = rows && list.length === 0;

  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState('asc');
  const toggleSort = React.useMemo(() => window.makeToggleSort(setSortKey, setSortDir), []);
  const sorted = React.useMemo(() => (
    sortKey ? [...list].sort(window.makeComparator(sortKey, sortDir, BUYER_SORT_EXTRACT)) : list
  ), [list, sortKey, sortDir]);

  const columns = window.useColumnPrefs('buyers', BUYER_COLUMNS, { rows: list, fitMax: 360 });
  const [passesOf, setPassesOf] = React.useState(null);
  const cellCtx = React.useMemo(() => ({ onPortfolio: setPortfolioOf, onTeam: setTeamOf, onPasses: setPassesOf }), []);

  const kpis = React.useMemo(() => {
    const c = (pred) => list.filter(pred).length;
    return {
      total:     list.length,
      agreement: c((b) => b.agreement_kind),
      expiring:  c((b) => {
        const d = buyerDaysToExpiry(b.pba_expiration_date);
        return d !== null && d >= 0 && d <= EXPIRY_SOON_DAYS;
      }),
      no_icp:    c((b) => !(b.preferred_industries || []).length && !b.investment_criteria),
    };
  }, [list]);

  const filtersDirty = debouncedQ || Object.keys(BUYER_FILTER_DEFAULTS)
    .some((k) => filters[k] !== BUYER_FILTER_DEFAULTS[k]);
  // number | null (nothing typed) | undefined (not a number) — drives the
  // live hint beside the Fits EBITDA box.
  const ebitdaParsed = window.parseNumInput(ebitdaInput);

  const sel = (label, key, options, width) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}:</span>
      <Combobox
        value={filters[key]}
        onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
        options={options}
        style={{ minWidth: width || 150 }}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <BgPageHeader
        breadcrumb={['Workspace', 'Buyers']}
        title="Buyers"
        actions={tab !== 'roster' ? null : (
          <>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search firm or domain…"
              style={{ width: 220, padding: '4px 10px', fontSize: 12,
                       border: '1px solid var(--border)', borderRadius: 6, height: 28, fontFamily: 'inherit' }} />
            {/* Every matching row is already in hand (the endpoint returns one
                page) — export exactly what the grid shows, in your column
                order and sort. */}
            <window.ExportMenu
              disabled={rows === null || !list.length}
              viewCount={list.length}
              onExport={(format) => {
                const xcols = window.exportColumnsFor(columns.cols, BUYER_EXPORT_ALWAYS);
                const records = window.exportRecords(xcols, sorted);
                const name = `buyers-${window.localISODate()}`;
                if (format === 'csv') window.exportCsv(`${name}.csv`, xcols, records);
                else window.exportJson(`${name}.json`, records);
              }}
            />
            <window.ColumnsMenu
              catalog={BUYER_COLUMNS}
              cols={columns.cols}
              toggle={columns.toggle}
              move={columns.move}
              reset={columns.reset}
            />
          </>
        )}
      />

      {/* Roster vs. what changed on their websites. Two different jobs — one
          is "who could buy this", the other is "who just started buying
          something new" — so they get tabs rather than a filter. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '0 16px',
                     borderBottom: '1px solid var(--border)', background: 'var(--surface)', ...bgTabStrip() }}>
        {[
          { key: 'roster',  label: 'Roster' },
          { key: 'changes', label: 'Site changes', badge: changeCount },
          { key: 'candidates', label: 'New buyers', badge: candCount },
          { key: 'acquisitions', label: 'Acquisitions', badge: acqCount },
          { key: 'intent', label: 'Intent', badge: intentCount },
          { key: 'match',   label: 'Match a deal' },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ appearance: 'none', background: 'none', border: 'none', cursor: 'pointer',
                     padding: '9px 12px', fontSize: 12.5, fontFamily: 'inherit',
                     color: tab === t.key ? 'var(--text)' : 'var(--text-3)',
                     fontWeight: tab === t.key ? 600 : 500,
                     borderBottom: `2px solid ${tab === t.key ? 'var(--accent)' : 'transparent'}`,
                     display: 'flex', alignItems: 'center', gap: 6, ...bgTabBtn() }}>
            {t.label}
            {(t.key === 'changes' || t.key === 'acquisitions' || t.key === 'intent') && t.badge > 0 && <Pill hue="amber" sz="sm">{t.badge}</Pill>}
            {t.key === 'candidates' && t.badge > 0 && <Pill hue="green" sz="sm">{t.badge}</Pill>}
          </button>
        ))}
      </div>

      {tab === 'changes' && <BuyerChangesPanel setCompanyId={setCompanyId} />}

      {tab === 'acquisitions' && <BuyerAcquisitionsPanel setCompanyId={setCompanyId} />}

      {tab === 'intent' && <BuyerIntentPanel setCompanyId={setCompanyId} />}

      {tab === 'candidates' && <BuyerCandidatesPanel setCompanyId={setCompanyId} onAccepted={reload} />}

      {tab === 'match' && <BuyerMatchPanel setCompanyId={setCompanyId} setDealId={setDealId} />}

      {teamOf && (
        <BuyerTeamDrawer buyer={teamOf} onClose={() => setTeamOf(null)} />
      )}

      {passesOf && (
        <BuyerPassesDrawer buyer={passesOf} onClose={() => setPassesOf(null)} />
      )}

      {portfolioOf && (
        <BuyerPortfolioDrawer
          buyer={portfolioOf}
          onClose={() => setPortfolioOf(null)}
          setCompanyId={setCompanyId}
        />
      )}

      {tab === 'roster' && (
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
        {/* Filter bar */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                       borderRadius: 8, padding: 10, marginBottom: 14,
                       display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          {sel('Tier', 'buyer_type', [
            { value: '', label: 'Any tier' },
            { value: 'preferred_buyer', label: 'Preferred buyers' },
            { value: 'peg', label: 'PEG' },
            { value: 'other', label: 'Other' },
          ])}
          {sel('Agreement', 'agreement_kind', [
            { value: '', label: 'Any' },
            { value: 'pb', label: 'Preferred Buyer Agreement' },
            { value: 'umbrella', label: 'Umbrella agreement' },
          ], 210)}
          {sel('Status', 'agreement_state', [
            { value: '', label: 'Any status' },
            { value: 'active', label: 'Under contract' },
            { value: 'expiring', label: `Expiring in ${EXPIRY_SOON_DAYS}d` },
            { value: 'expired', label: 'Expired' },
            { value: 'none', label: 'No agreement' },
          ], 175)}
          {sel('Blast', 'blast_status', [
            { value: '', label: 'Any' },
            { value: 'Yes', label: 'On PB blast' },
            { value: 'Expired', label: 'Dropped from blast' },
          ], 165)}
          {sel('Owner', 'owner_id', [
            { value: '', label: 'Any owner' },
            // Orphaned relationships are a filter of their own — 24 PB
            // agreements are owned by someone who has left.
            { value: '__departed', label: 'Owner has left' },
            ...users.map((u) => ({
              value: u.id,
              label: u.active === false ? `${u.full_name} (left)` : u.full_name,
            })),
          ], 180)}
          {sel('ICP', 'has_icp', [
            { value: '', label: 'Any' },
            { value: 'yes', label: 'Has criteria' },
            { value: 'no', label: 'Missing criteria' },
          ], 155)}
          {sel('Domain', 'domain_suspect', [
            { value: '', label: 'Any' },
            { value: 'yes', label: 'Looks wrong' },
            { value: 'no', label: 'Reads OK / not read' },
          ], 175)}
          {/* "Has a portfolio we could read" — a firm whose page we could
              not read counts as No here, and the drawer says why. */}
          {sel('Portfolio', 'has_portfolio', [
            { value: '', label: 'Any' },
            { value: 'yes', label: 'Portfolio on file' },
            { value: 'no', label: 'No portfolio read' },
          ], 175)}
          {sel('In SS', 'in_sourcescrub', [
            { value: '', label: 'Any' },
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'unknown', label: 'Not evaluated' },
          ], 145)}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Industry:</span>
            <input value={industryInput} onChange={(e) => setIndustryInput(e.target.value)}
              placeholder="e.g. roofing"
              style={{ width: 130, padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)',
                       borderRadius: 6, height: 26, fontFamily: 'inherit' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Fits EBITDA:</span>
            <input value={ebitdaInput} onChange={(e) => setEbitdaInput(e.target.value)}
              onBlur={() => { if (typeof ebitdaParsed === 'number') setEbitdaInput(window.formatNumInput(ebitdaParsed)); }}
              placeholder="e.g. 4M" inputMode="decimal"
              title="Show buyers whose stated EBITDA band contains this figure. Absolute dollars — 4M, 100k and 4000000 all mean what they say. Buyers with no stated band are included — a missing band means unknown, not a mismatch."
              style={{ width: 92, padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)',
                       borderRadius: 6, height: 26, fontFamily: 'inherit' }} />
            {/* This box read as $MM until now, so the parsed figure has to be
                on screen, not just in the tooltip. Shown whenever there's
                something typed (not focus-only like the drawer hints) —
                that's the whole guard against a bare "4" quietly meaning $4
                to someone who remembers it meaning $4M. */}
            {ebitdaInput.trim() !== '' && (
              <span style={{ fontSize: 11, whiteSpace: 'nowrap',
                             color: ebitdaParsed === undefined ? 'var(--rose-fg)' : 'var(--text-4)' }}>
                {ebitdaParsed === undefined ? 'not a number' : `= ${window.fmtMoney(ebitdaParsed)}`}
              </span>
            )}
          </div>
          <div style={{ flex: 1 }} />
          {filtersDirty && (
            <Btn size="xs" variant="ghost"
              onClick={() => { setFilters({ ...BUYER_FILTER_DEFAULTS }); setQ(''); setIndustryInput(''); setEbitdaInput(''); }}>
              Clear
            </Btn>
          )}
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { label: 'Showing',          value: kpis.total },
            { label: 'Under agreement',  value: kpis.agreement },
            { label: `Expiring in ${EXPIRY_SOON_DAYS}d`, value: kpis.expiring },
            { label: 'No stated ICP',    value: kpis.no_icp },
          ].map((k) => (
            <div key={k.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                                          borderRadius: 6, padding: '8px 12px' }}>
              <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                             letterSpacing: '0.05em', fontWeight: 500 }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>
                {rows === null ? '…' : k.value.toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        {/* Table — header and body both map over columns.cols so they can't
            drift apart. overflowY stays 'hidden' (not 'auto'): WindowedRows
            walks up to the nearest overflow-y auto/scroll ancestor for its
            scroll events, and that must resolve to the page scroller above,
            not this card. */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                       borderRadius: 8, overflowX: 'auto', overflowY: 'hidden' }}>
          {rows === null && (
            <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
          )}
          {empty && (
            <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)' }}>
              No buyers match the current filters. Adjust them above, or re-import the roster with{' '}
              <code className="mono" style={{ fontSize: 11, padding: '1px 4px', background: 'var(--surface-2)', borderRadius: 3 }}>
                python scripts/import_buyer_report.py "Buyer Report.xlsx"
              </code>.
            </div>
          )}
          {rows && list.length > 0 && (
            <div style={{ minWidth: 'max-content' }}>
              <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)',
                            fontSize: 11, color: 'var(--text-3)', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                            borderBottom: '1px solid var(--border)' }}>
                {columns.cols.map((c) => (
                  <window.SortableHeader key={c.key} col={c}
                    sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                    sortable={Boolean(BUYER_SORT_EXTRACT[c.key])}
                    onResizeStart={columns.startResize(c.key)}
                  />
                ))}
              </div>
              <window.WindowedRows rows={sorted} renderRow={(b) => (
                <BuyerRow key={b.buyer_id} b={b} cols={columns.cols} cellCtx={cellCtx}
                  onOpen={(b) => setCompanyId(b.company_id)} />
              )} />
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

// ============ GPP PARTNERS ============
// Growth Partner Program — IPro's curated network of wealth advisors,
// accountants, attorneys, and bankers who refer deals. Each partner is a
// row in gpp_partners (1:1 with a contact). The page is filter/sort over
// the denormalized /api/gpp-partners list.
const PARTNER_TYPE_LABEL = {
  wealth_manager: 'Wealth manager',
  accountant:     'Accountant',
  attorney:       'Attorney',
  banker:         'Banker',
  other:          'Other',
};
const NURTURE_HUE = {
  active: 'green', cooling: 'amber', cold: 'rose', inactive: 'slate',
};

// Known funnel-stage values pulled from real DC distributions. We could
// derive these from the data but a fixed list keeps the dropdowns stable
// (and avoids surprises when a new partner lands with a typo'd value).
// Funnel boundary (2026-09-15): 'Exploratory Call Scheduled' — the discovery
// call — is the LAST pre-program step, so it is a lead status; 'Attended
// Call - Moving Into GPP' is the FIRST in-program stage. DC files both on
// the other field; the import translates (migrate_gpp_partners.
// normalize_funnel) and 202609151800 moved the old rows.
const LEAD_STATUS_OPTIONS = [
  'Call Scheduled', 'Follow-Up Scheduled', 'Exploratory Call Scheduled',
  'Did Not Attend Call', 'Stalled',
  'Attended Call - DQ', 'Cancelled Call - DQ',
  'Attended - Not Interested', 'Lost',
];
// In-program stages carry the Growth Partner portal's names (Zach + Chris,
// 2026-09-23): Exploratory Call Complete → Network Insights Call Complete →
// Network Matrix Call Complete → Month 1..12 Check In Complete → Fully
// Onboarded. DC's GPPStage labels are translated on import; migrations
// 202609211600 + 202609232300 renamed the existing rows.
const GPP_STAGE_OPTIONS = [
  'Unassigned',
  'Exploratory Call Complete', 'Network Insights Call Complete', 'Network Matrix Call Complete',
  ...Array.from({ length: 12 }, (_, i) => `Month ${i + 1} Check In Complete`),
  'Fully Onboarded',
  'Disqualified', 'Inactive',
];

// Owner filter opens on the signed-in user's own partners (sticky opt-out) —
// same personalization as Referral Partners; see makeOwnerScope.
const GPP_OWNER_SCOPE = window.makeOwnerScope('atlas:gpp-owner-scope');

// ────────────────────────────────────────────────────────────────────────
// Standard grid stack (same as People/Companies/Engagements — and Referral
// Partners, whose retrofit this mirrors): ONE column catalog mapped by both
// header and body, per-user show/hide/reorder/resize via
// useColumnPrefs('gpp'), clickable-header sorting, exports that mirror the
// visible layout. All rows are in hand (≤500 partners) → client-side sort.
//
// gpp_partners is an Atlas-owned table (no DC mirror rewrites it), so every
// field the PATCH accepts is safe to inline-edit — nothing undoes the edit
// on the next sync pass.

// A date-only value ("2026-09-21") is a calendar day: parse it at local noon
// so it never renders as the previous evening in US time zones.
const fmtGppDate = (iso) => iso
  ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso + 'T12:00:00' : iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

// Pill hue for the in-program stage: green for onboarded/check-in, amber for
// early-program (moving into GPP through the profile review), rose for
// disqualified/inactive.
const gppStageHue = (v) => {
  const s = (v || '').toLowerCase();
  return /(fully onboarded|monthly check-in|month \d+ check in)/.test(s) ? 'green' :   // 'month N check in complete' matches too
         /(disqualified|inactive)/.test(s)             ? 'rose'  :
         /(exploratory call complete|network insights call|network matrix call|kickoff|profiles and trust|engagement review|moving into gpp|network profile)/.test(s) ? 'amber' : 'slate';
};
// Lead-status hue: amber for in-flight (any scheduled call, incl. the
// exploratory/discovery call), rose for DQ/lost, slate for stalled.
const gppLeadHue = (v) => {
  const s = (v || '').toLowerCase();
  return /(dq|not interested|lost)/.test(s) ? 'rose'  :
         /(stalled|did not attend)/.test(s) ? 'slate' :
         /(scheduled|follow-up)/.test(s)    ? 'amber' : 'slate';
};

const GPP_SORT_EXTRACT = {
  partner:      (p) => (p.contact_name || '').toLowerCase() || null,
  firm:         (p) => (p.company_name || '').toLowerCase() || null,
  owner:        (p) => (p.dealmaker_name || '').toLowerCase() || null,
  // Funnel columns rank in funnel order (the fixed option lists), not
  // alphabetically; a blank or unrecognized value sinks like a null.
  leadStatus:   (p) => { const i = LEAD_STATUS_OPTIONS.indexOf(p.referral_lead_status); return i < 0 ? null : i; },
  stage:        (p) => { const i = GPP_STAGE_OPTIONS.indexOf(p.gpp_stage); return i < 0 ? null : i; },
  refs:         (p) => p.referral_count || 0,
  lastContact:  (p) => p.last_contact_date || null,
  callDate:     (p) => p.call_date || null,
  created:      (p) => p.created_at || null,
  updated:      (p) => p.updated_at || null,
  type:         (p) => (PARTNER_TYPE_LABEL[p.partner_type] || p.partner_type || '').toLowerCase() || null,
  nurture:      (p) => (p.nurture_status || '').toLowerCase() || null,
  email:        (p) => (p.contact_email || '').toLowerCase() || null,
  bizOwners:    (p) => { const n = parseInt(p.number_of_business_owners, 10); return Number.isNaN(n) ? null : n; },
  portalOwners: (p) => (p.portal_owner_count == null ? null : p.portal_owner_count),
  portalLogin:  (p) => p.portal_last_login_at || null,
  callAttended: (p) => (p.gpp_call_attended == null ? null : (p.gpp_call_attended ? 0 : 1)),  // attended first
  joined:       (p) => p.joined_date || null,
  lastReferral: (p) => p.last_referral_date || null,
  refsDs:       (p) => p.referrals_to_ds || 0,
  refsClosed:   (p) => p.referrals_closed || 0,
  notes:        (p) => (p.notes || '').toLowerCase() || null,
};

// Simple one-line cell — same contract as the other catalog grids: size to
// col.w, rule the right divider unless last.
const gppCell = (col, content, opts = {}) => (
  <div key={col.key}
    onClick={opts.stop ? (e) => e.stopPropagation() : undefined}
    title={opts.title}
    style={{ ...(opts.stop ? window.EDIT_CELL_WRAP : null), width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center',
             justifyContent: opts.right ? 'flex-end' : 'flex-start',
             minWidth: 0, fontSize: 12, color: opts.color || 'var(--text-2)',
             borderRight: col.last ? 'none' : '1px solid var(--border)',
             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    className={opts.right ? 'num' : ''}>
    {content}
  </div>
);

const GPP_COLUMNS = [
  {
    key: 'partner', label: 'Partner', fixed: true, extra: 50,
    accessor: (p) => p.contact_name,
    xget: (p) => p.contact_name || '',
    // Email + type ride along in the export whenever Partner is visible
    // (always — it's the fixed identity column), preserving the old file
    // shape while both live in the subline/pill rather than own columns.
    xextra: [
      { key: 'contact_email', label: 'Email', get: (p) => p.contact_email || '' },
      { key: 'partner_type',  label: 'Type',  get: (p) => p.partner_type || '' },
    ],
    render: (p, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '6px 10px', minWidth: 0,
                                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                  borderRight: col.last ? 'none' : '1px solid var(--border)' }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.contact_name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Pill hue="teal" sz="sm">{PARTNER_TYPE_LABEL[p.partner_type] || p.partner_type}</Pill>
        </div>
      </div>
    ),
  },
  {
    key: 'firm', label: 'Firm', extra: 20,
    accessor: (p) => p.company_name || '',
    xextra: [{ key: 'number_of_business_owners', label: 'Business owners', get: (p) => p.number_of_business_owners ?? '' }],
    render: (p, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '6px 10px', minWidth: 0,
                                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                  borderRight: col.last ? 'none' : '1px solid var(--border)' }}>
        <div style={{ fontSize: 12.5, color: p.company_name ? 'var(--text-2)' : 'var(--text-4)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.company_name || '—'}
        </div>
        {p.number_of_business_owners && (
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {p.number_of_business_owners} biz owners
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'owner', label: 'Owner', extra: 24,
    accessor: (p) => p.dealmaker_name || '',
    xget: (p) => p.dealmaker_name || '',
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="user" fill value={p.assigned_dealmaker_id} users={ctx.users}
        fallbackLabel={p.dealmaker_name}
        onSave={(assigned_dealmaker_id) => {
          // A partner isn't real without an owner (the promote flow enforces
          // it too) — refuse the editor's "Unassigned" option so the cell
          // reverts rather than writing a null the funnel KPIs would drop.
          if (!assigned_dealmaker_id) {
            pushToast({ type: 'error', title: 'GPP partners need an owner',
                        body: 'Pick a dealmaker — remove the partner from their contact page instead.' });
            return Promise.reject(new Error('owner required'));
          }
          return ctx.onSave(p.id, { assigned_dealmaker_id });
        }} />
    ), { stop: true }),
  },
  {
    key: 'leadStatus', label: 'Lead status', extra: 12,
    desc: 'Pre-program funnel — where the lead is before onboarding.',
    accessor: (p) => p.referral_lead_status || '',
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="select" fill value={p.referral_lead_status}
        options={[{ value: '', label: '—' },
                  ...LEAD_STATUS_OPTIONS.map((s) => ({ value: s, label: s, hue: gppLeadHue(s) }))]}
        displayAs={p.referral_lead_status
          ? <Pill hue={gppLeadHue(p.referral_lead_status)} sz="sm">{p.referral_lead_status}</Pill>
          : undefined}
        onSave={(referral_lead_status) => ctx.onSave(p.id, { referral_lead_status })} />
    ), { stop: true }),
  },
  {
    key: 'stage', label: 'GPP stage', extra: 12,
    desc: 'In-program funnel — onboarding through the monthly check-ins.',
    accessor: (p) => p.gpp_stage || '',
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="select" fill value={p.gpp_stage}
        options={[{ value: '', label: '—' },
                  ...GPP_STAGE_OPTIONS.map((s) => ({ value: s, label: s, hue: gppStageHue(s) }))]}
        displayAs={p.gpp_stage
          ? <Pill hue={gppStageHue(p.gpp_stage)} sz="sm">{p.gpp_stage}</Pill>
          : undefined}
        onSave={(gpp_stage) => ctx.onSave(p.id, { gpp_stage })} />
    ), { stop: true }),
  },
  {
    key: 'refs', label: 'Refs', align: 'right',
    desc: 'Referrals this partner has sent (not editable).',
    accessor: (p) => String(p.referral_count || 0),
    xget: (p) => p.referral_count ?? 0,
    render: (p, ctx, col) => gppCell(col, p.referral_count || 0,
      { right: true, color: (p.referral_count || 0) > 0 ? 'var(--text)' : 'var(--text-4)' }),
  },
  {
    key: 'callDate', label: 'Call date', extra: 16,
    desc: 'The GPP call — booked when the partner is created, so it can be in the future ("upcoming"). Editable.',
    accessor: (p) => fmtGppDate(p.call_date),
    xget: (p) => p.call_date || '',
    render: (p, ctx, col) => {
      const day = p.call_date ? p.call_date.slice(0, 10) : null;
      const upcoming = day && day > window.localISODate();
      return gppCell(col, (
        <window.EditableCell type="date" fill
          value={day}
          displayAs={(
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: day ? 'var(--text-2)' : 'var(--text-4)' }}>{fmtGppDate(p.call_date)}</span>
              {upcoming && <Pill hue="amber" sz="sm">upcoming</Pill>}
            </span>
          )}
          onSave={(call_date) => ctx.onSave(p.id, { call_date })} />
      ), { stop: true });
    },
  },
  {
    key: 'lastContact', label: 'Last contact', extra: 16,
    desc: 'Last real touch (never a future date). Editable — the no-show pill means the last scheduled call was not attended.',
    accessor: (p) => fmtGppDate(p.last_contact_date),
    xget: (p) => p.last_contact_date || '',
    xextra: [{ key: 'gpp_call_attended', label: 'GPP call attended', get: (p) => (p.gpp_call_attended ? 'yes' : '') }],
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="date" fill
        value={p.last_contact_date ? p.last_contact_date.slice(0, 10) : null}
        displayAs={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: p.last_contact_date ? 'var(--text-2)' : 'var(--text-4)' }}>
              {fmtGppDate(p.last_contact_date)}
            </span>
            {p.gpp_call_attended === false && <Pill hue="rose" sz="sm">no-show</Pill>}
          </span>
        )}
        onSave={(last_contact_date) => ctx.onSave(p.id, { last_contact_date })} />
    ), { stop: true }),
  },
  {
    // Record created / updated. gpp_partners is Atlas-owned (not a DC
    // mirror), so its own timestamps are the real ones — source="atlas".
    key: 'created', label: 'Created',
    accessor: (p) => fmtGppDate(p.created_at),
    xget: (p) => p.created_at || '',
    render: (p, ctx, col) => <window.RecordDateCell key={col.key} row={p} kind="created" source="atlas" w={col.w} border />,
  },
  {
    key: 'updated', label: 'Updated',
    accessor: (p) => fmtGppDate(p.updated_at),
    xget: (p) => p.updated_at || '',
    render: (p, ctx, col) => <window.RecordDateCell key={col.key} row={p} kind="modified" source="atlas" w={col.w} border last={col.last} />,
  },

  // ── Off by default, one click away in the Columns menu.
  {
    key: 'type', label: 'Type', def: false, group: 'Classification',
    desc: 'Partner profession — also shown as the pill under the name. Editable.',
    accessor: (p) => PARTNER_TYPE_LABEL[p.partner_type] || p.partner_type || '',
    xskip: true,  // already exported via the Partner column's xextra
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="select" fill value={p.partner_type}
        options={Object.entries(PARTNER_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
        onSave={(partner_type) => ctx.onSave(p.id, { partner_type })} />
    ), { stop: true }),
  },
  {
    key: 'nurture', label: 'Nurture', def: false, group: 'Classification',
    desc: 'Relationship warmth. Editable.',
    accessor: (p) => p.nurture_status || '',
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="select" fill value={p.nurture_status}
        options={Object.keys(NURTURE_HUE).map((v) => ({ value: v, label: v, hue: NURTURE_HUE[v] }))}
        onSave={(nurture_status) => ctx.onSave(p.id, { nurture_status })} />
    ), { stop: true }),
  },
  {
    key: 'checkin', label: 'Check-in', def: true, group: 'Classification',
    desc: 'How often the dealmaker gets a check-in call task for this partner — completing it schedules the next. Editable.',
    accessor: (p) => (window.HOLD_CADENCE_LABEL || {})[p.checkin_cadence] || '',
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="select" fill value={p.checkin_cadence || null}
        options={(window.HOLD_CADENCE_OPTIONS || []).map((o) => ({ ...o, hue: o.value === 'none' ? 'slate' : 'blue' }))}
        onSave={(checkin_cadence) => ctx.onSave(p.id, { checkin_cadence: checkin_cadence || 'none' })} />
    ), { stop: true }),
  },
  {
    key: 'email', label: 'Email', def: false, group: 'Contact details',
    accessor: (p) => p.contact_email || '',
    xskip: true,  // already exported via the Partner column's xextra
    render: (p, ctx, col) => gppCell(col, p.contact_email || '—',
      { color: p.contact_email ? 'var(--text-2)' : 'var(--text-4)' }),
  },
  {
    key: 'bizOwners', label: 'Biz owners', def: false, group: 'Details', align: 'right',
    desc: 'Number of business owners in the partner\'s book. Editable.',
    accessor: (p) => p.number_of_business_owners || '',
    xskip: true,  // already exported via the Firm column's xextra
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="text" fill value={p.number_of_business_owners || ''}
        onSave={(number_of_business_owners) => ctx.onSave(p.id, { number_of_business_owners })} />
    ), { stop: true }),
  },
  {
    key: 'portalOwners', label: 'Portal owners', group: 'Details', align: 'right',
    desc: 'Business owners entered in their Network Matrix on the Growth Partner portal. "—" = not in the portal yet.',
    accessor: (p) => (p.portal_owner_count == null ? '' : String(p.portal_owner_count)),
    render: (p, ctx, col) => gppCell(col, p.portal_owner_count == null ? '—' : p.portal_owner_count,
      { color: p.portal_owner_count ? 'var(--text)' : 'var(--text-4)' }),
  },
  {
    key: 'portalLogin', label: 'Portal login', def: false, group: 'Details', align: 'right',
    desc: 'Last time they signed in to the Growth Partner portal.',
    accessor: (p) => (p.portal_last_login_at ? fmtGppDate(p.portal_last_login_at) : ''),
    render: (p, ctx, col) => gppCell(col, p.portal_last_login_at ? fmtGppDate(p.portal_last_login_at) : 'never',
      { color: p.portal_last_login_at ? 'var(--text-2)' : 'var(--text-4)' }),
  },
  {
    key: 'callAttended', label: 'GPP call', def: false, group: 'Details',
    desc: 'Did they attend their GPP call? Editable.',
    accessor: (p) => (p.gpp_call_attended == null ? '' : (p.gpp_call_attended ? 'attended' : 'no-show')),
    xskip: true,  // already exported via the Last contact column's xextra
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="select" fill
        value={p.gpp_call_attended == null ? '' : (p.gpp_call_attended ? 'yes' : 'no')}
        options={[{ value: '', label: '—' },
                  { value: 'yes', label: 'attended', hue: 'green' },
                  { value: 'no',  label: 'no-show',  hue: 'rose' }]}
        onSave={(v) => ctx.onSave(p.id, { gpp_call_attended: v == null ? null : v === 'yes' })} />
    ), { stop: true }),
  },
  {
    key: 'joined', label: 'Joined', def: false, group: 'Dates',
    desc: 'When they joined the program. Editable.',
    accessor: (p) => fmtGppDate(p.joined_date),
    xget: (p) => p.joined_date || '',
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="date" fill
        value={p.joined_date ? p.joined_date.slice(0, 10) : null}
        onSave={(joined_date) => ctx.onSave(p.id, { joined_date })} />
    ), { stop: true }),
  },
  {
    key: 'lastReferral', label: 'Last referral', def: false, group: 'Referrals',
    desc: 'Date of the most recent referral they sent.',
    accessor: (p) => fmtGppDate(p.last_referral_date),
    xget: (p) => p.last_referral_date || '',
    render: (p, ctx, col) => gppCell(col, fmtGppDate(p.last_referral_date),
      { color: p.last_referral_date ? 'var(--text-2)' : 'var(--text-4)' }),
  },
  {
    key: 'refsDs', label: 'Refs → D/S', def: false, group: 'Referrals', align: 'right',
    desc: 'Their referrals that reached a D/S call.',
    accessor: (p) => String(p.referrals_to_ds || 0),
    xget: (p) => p.referrals_to_ds ?? 0,
    render: (p, ctx, col) => gppCell(col, p.referrals_to_ds || 0,
      { right: true, color: (p.referrals_to_ds || 0) > 0 ? 'var(--text)' : 'var(--text-4)' }),
  },
  {
    key: 'refsClosed', label: 'Refs closed', def: false, group: 'Referrals', align: 'right',
    desc: 'Their referrals that closed.',
    accessor: (p) => String(p.referrals_closed || 0),
    xget: (p) => p.referrals_closed ?? 0,
    render: (p, ctx, col) => gppCell(col, p.referrals_closed || 0,
      { right: true, color: (p.referrals_closed || 0) > 0 ? 'var(--text)' : 'var(--text-4)' }),
  },
  {
    key: 'notes', label: 'Notes', def: false, group: 'Details',
    desc: 'Program notes on the partner row. Editable.',
    accessor: (p) => p.notes || '',
    render: (p, ctx, col) => gppCell(col, (
      <window.EditableCell type="text" fill value={p.notes || ''}
        onSave={(notes) => ctx.onSave(p.id, { notes })} />
    ), { stop: true }),
  },
];

// Identifier columns appended to every export regardless of layout.
const GPP_EXPORT_ALWAYS = [
  { key: 'id',         label: 'Atlas id',   get: (p) => p.id },
  { key: 'contact_id', label: 'Contact id', get: (p) => p.contact_id },
];

// Memoized row — an inline cell edit swaps ONE row object in state, so only
// that row re-renders (same pattern as PersonRow / RpRow).
const GppRow = React.memo(({ p, cols, cellCtx, onOpen }) => (
  <div
    onClick={() => onOpen(p.contact_id)}
    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
    style={{ display: 'flex', minHeight: 44, alignItems: 'stretch',
             borderBottom: '1px solid var(--border)', background: 'var(--surface)',
             cursor: 'pointer' }}>
    {cols.map((col) => col.render(p, cellCtx, col))}
  </div>
));

const GppView = ({ setContactId, activeUser }) => {
  // 'partners' = the partner grid; 'scoreboard' = stages, seasoning and
  // referral → closed-business attribution (gpp_scoreboard.jsx). A
  // #/gpp?tab=scoreboard link wins; otherwise the last tab you picked.
  const [tab, setTab] = React.useState(() => {
    const m = /[?&]tab=([a-z]+)/.exec(window.location.hash || '');
    if (m && (m[1] === 'partners' || m[1] === 'scoreboard')) return m[1];
    try { return localStorage.getItem('atlas:gpp-tab') === 'scoreboard' ? 'scoreboard' : 'partners'; } catch { return 'partners'; }
  });
  const pickTab = (t) => {
    setTab(t);
    try { localStorage.setItem('atlas:gpp-tab', t); } catch { /* storage blocked — the tab still switches */ }
  };
  const [partners, setPartners] = React.useState(null);
  // Growth Partner portal address (GPP_PORTAL_URL) — arrives with the roster.
  const [portalUrl, setPortalUrl] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [users, setUsers] = React.useState([]);
  // "Add partner" is a two-step chain: create the contact (type gpp), then
  // the promote form (owner, partner type, funnel stage) makes it a partner.
  const [addingContact, setAddingContact]   = React.useState(false);
  const [promoteContact, setPromoteContact] = React.useState(null);
  const [filters, setFilters] = React.useState(() => ({
    partner_type:         '',
    nurture_status:       '',
    dealmaker_id:         GPP_OWNER_SCOPE.get(activeUser?.role) === 'mine' ? (activeUser?.id || '') : '',
    referral_lead_status: '',  // pre-program funnel — client-side filter
    gpp_stage:            '',  // in-program funnel — client-side filter
  }));
  // activeUser can arrive after mount on a cold boot. Only the scope-derived
  // default may move the filter — once Owner (or Clear) has been touched,
  // whatever is on screen wins.
  const ownerTouched = React.useRef(false);
  React.useEffect(() => {
    if (ownerTouched.current || !activeUser?.id) return;
    setFilters((f) => {
      const want = GPP_OWNER_SCOPE.get(activeUser.role) === 'mine' ? activeUser.id : '';
      return f.dealmaker_id === want ? f : { ...f, dealmaker_id: want };
    });
  }, [activeUser?.id, activeUser?.role]);
  const changeOwner = (v) => {
    ownerTouched.current = true;
    if (v === '') GPP_OWNER_SCOPE.set('any');
    else if (activeUser?.id && v === activeUser.id) GPP_OWNER_SCOPE.set('mine');
    setFilters((f) => ({ ...f, dealmaker_id: v }));
  };

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  React.useEffect(() => {
    window.AtlasApi.listTeamMembers()
      .then((m) => setUsers(Array.isArray(m) ? m.filter((u) => u.active !== false) : []))
      .catch(() => setUsers([]));
  }, []);

  const reload = React.useCallback(() => {
    setPartners(null);
    const params = { limit: 500 };
    if (debouncedQ) params.q = debouncedQ;
    if (filters.partner_type)   params.partner_type   = filters.partner_type;
    if (filters.nurture_status) params.nurture_status = filters.nurture_status;
    if (filters.dealmaker_id)   params.dealmaker_id   = filters.dealmaker_id;
    window.AtlasApi.listGppPartners(params)
      .then((r) => {
        setPartners((r && r.items) || []);
        if (r && r.portal_url) setPortalUrl(r.portal_url);
      })
      .catch(() => setPartners([]));
  }, [debouncedQ, filters]);
  React.useEffect(() => { reload(); }, [reload]);

  // Apply client-side funnel filters (these aren't part of the server-side
  // params yet — the API doesn't filter on these columns, so we narrow
  // after fetch. Cheap for ≤a few hundred partners.)
  const filtered = React.useMemo(() => {
    let r = partners || [];
    if (filters.referral_lead_status)
      r = r.filter((p) => p.referral_lead_status === filters.referral_lead_status);
    if (filters.gpp_stage)
      r = r.filter((p) => p.gpp_stage === filters.gpp_stage);
    return r;
  }, [partners, filters.referral_lead_status, filters.gpp_stage]);
  const empty = partners && filtered.length === 0;

  // Per-user column layout (105) — and, riding the same saved blob, the
  // header sort, so "I always want this by created date" survives a reload
  // and follows the person to another browser.
  const columns = window.useColumnPrefs('gpp', GPP_COLUMNS, {
    rows: filtered, fitMax: 360,
  });

  // Column sort — client-side, every row is in hand. Default (no saved sort)
  // keeps the server order. A saved key whose column has since gone away
  // compares equal for every row (makeComparator), i.e. falls back to the
  // server order rather than crashing.
  const sortKey = columns.sort ? columns.sort.key : null;
  const sortDir = columns.sort ? columns.sort.dir : 'asc';
  const toggleSort = columns.toggleSort;
  const sorted = React.useMemo(() => (
    sortKey
      ? [...filtered].sort(window.makeComparator(sortKey, sortDir, GPP_SORT_EXTRACT))
      : filtered
  ), [filtered, sortKey, sortDir]);

  // Inline cell editing — PATCH the partner row, merge the server's response
  // back in (dealmaker_name and updated_at come back resolved). Only the
  // edited row object changes identity, so the memoized GppRow re-renders
  // alone. gpp_partners is Atlas-owned, so no sync pass undoes the edit.
  const saveGpp = React.useCallback(async (id, patch) => {
    const updated = await window.AtlasApi.updateGppPartner(id, patch);
    setPartners((prev) => (prev || []).map((x) => (x.id === id ? { ...x, ...updated } : x)));
  }, []);

  // One object so the memoized row can't capture a stale per-cell closure.
  const cellCtx = React.useMemo(() => ({ onSave: saveGpp, users }), [saveGpp, users]);

  // KPI strip — at-a-glance counts under the current filter combination.
  const kpis = React.useMemo(() => {
    const rows = filtered || [];
    const c = (pred) => rows.filter(pred).length;
    // "Onboarded" — partners actually in-program (gpp_stage in active states).
    const onboarded = c((p) => p.gpp_stage && /^(fully onboarded|monthly check-in \d+|month \d+ check in( complete)?)$/i.test(p.gpp_stage));
    return {
      total:     rows.length,
      onboarded,
      in_lead:   c((p) => !p.gpp_stage || /^unassigned$/i.test(p.gpp_stage)),
      referred:  c((p) => (p.referral_count || 0) > 0),
    };
  }, [filtered]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <BgPageHeader
        breadcrumb={['Workspace', 'GPP Partners']}
        title="GPP Partners"
        actions={(
          <>
            {tab === 'partners' && (<>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or firm…"
              style={{
                width: 240, padding: '4px 10px', fontSize: 12,
                border: '1px solid var(--border)', borderRadius: 6, height: 28, fontFamily: 'inherit',
              }} />
            {/* Rows are all in hand (≤500 partners) — export what the
                current search + filters show, mirroring the grid: the
                columns you can see, in your order and sort. */}
            <window.ExportMenu
              disabled={partners === null || !filtered.length}
              viewCount={filtered.length}
              onExport={(format) => {
                const xcols = window.exportColumnsFor(columns.cols, GPP_EXPORT_ALWAYS);
                const records = window.exportRecords(xcols, sorted);
                const name = `gpp-partners-${window.localISODate()}`;
                if (format === 'csv') window.exportCsv(`${name}.csv`, xcols, records);
                else window.exportJson(`${name}.json`, records);
              }}
            />
            <window.ColumnsMenu
              catalog={GPP_COLUMNS}
              cols={columns.cols}
              toggle={columns.toggle}
              move={columns.move}
              reset={columns.reset}
            />
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={11} />} onClick={() => setAddingContact(true)}>
              Add partner
            </Btn>
            </>)}
            {/* The partners' own portal (Network Matrix, stages, emails) —
                on both tabs, in a new tab so Atlas stays open. */}
            {portalUrl && (
              <Btn size="sm" variant="outline" icon={<Icon.External size={11} />}
                onClick={() => window.open(portalUrl, '_blank', 'noopener')}>
                Open portal
              </Btn>
            )}
          </>
        )}
      />

      {/* The partner grid vs. the program scoreboard — same tab chrome as
          the Buyers page. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '0 16px',
                     borderBottom: '1px solid var(--border)', background: 'var(--surface)', ...bgTabStrip() }}>
        {[
          { key: 'partners',   label: 'Partners' },
          { key: 'scoreboard', label: 'Scoreboard' },
        ].map((t) => (
          <button key={t.key} onClick={() => pickTab(t.key)}
            style={{ appearance: 'none', background: 'none', border: 'none', cursor: 'pointer',
                     padding: '9px 12px', fontSize: 12.5, fontFamily: 'inherit',
                     color: tab === t.key ? 'var(--text)' : 'var(--text-3)',
                     fontWeight: tab === t.key ? 600 : 500,
                     borderBottom: `2px solid ${tab === t.key ? 'var(--accent)' : 'transparent'}`,
                     display: 'flex', alignItems: 'center', gap: 6, ...bgTabBtn() }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'scoreboard' && <window.GppScoreboard setContactId={setContactId} />}

      {tab === 'partners' && (
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
        {/* Filter bar */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                       borderRadius: 8, padding: 10, marginBottom: 14,
                       display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Type:</span>
            <Combobox
              value={filters.partner_type}
              onChange={(e) => setFilters((f) => ({ ...f, partner_type: e.target.value }))}
              options={[
                { value: '', label: 'Any type' },
                ...Object.entries(PARTNER_TYPE_LABEL).map(([v, l]) => ({ value: v, label: l })),
              ]}
              style={{ minWidth: 160 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Nurture:</span>
            <Combobox
              value={filters.nurture_status}
              onChange={(e) => setFilters((f) => ({ ...f, nurture_status: e.target.value }))}
              options={[
                { value: '', label: 'Any status' },
                { value: 'active',   label: 'Active' },
                { value: 'cooling',  label: 'Cooling' },
                { value: 'cold',     label: 'Cold' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              style={{ minWidth: 140 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Owner:</span>
            <Combobox
              value={filters.dealmaker_id}
              onChange={(e) => changeOwner(e.target.value)}
              options={[
                { value: '', label: 'Any owner' },
                ...users.map((u) => ({ value: u.id, label: u.full_name })),
              ]}
              style={{ minWidth: 180 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Lead status:</span>
            <Combobox
              value={filters.referral_lead_status}
              onChange={(e) => setFilters((f) => ({ ...f, referral_lead_status: e.target.value }))}
              options={[
                { value: '', label: 'Any' },
                ...LEAD_STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
              ]}
              style={{ minWidth: 220 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>GPP stage:</span>
            <Combobox
              value={filters.gpp_stage}
              onChange={(e) => setFilters((f) => ({ ...f, gpp_stage: e.target.value }))}
              options={[
                { value: '', label: 'Any' },
                ...GPP_STAGE_OPTIONS.map((s) => ({ value: s, label: s })),
              ]}
              style={{ minWidth: 200 }}
            />
          </div>
          <div style={{ flex: 1 }} />
          <Btn size="xs" variant="ghost"
            onClick={() => {
              // Clear is a one-visit "show me everything" — it doesn't
              // change the remembered owner default.
              ownerTouched.current = true;
              setFilters({
                partner_type: '', nurture_status: '', dealmaker_id: '',
                referral_lead_status: '', gpp_stage: '',
              }); setQ('');
            }}>
            Clear
          </Btn>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { label: 'Showing',         value: kpis.total },
            { label: 'Onboarded',       value: kpis.onboarded },
            { label: 'In lead funnel',  value: kpis.in_lead },
            { label: 'Have referred',   value: kpis.referred },
          ].map((k) => (
            <div key={k.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                                          borderRadius: 6, padding: '8px 12px' }}>
              <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                             letterSpacing: '0.05em', fontWeight: 500 }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>
                {partners === null ? '…' : k.value.toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        {/* Table — header and body both map over columns.cols, so they can't
            drift apart. Defaults mirror DC's "Growth Partner Tracker" layout
            (Partner · Firm · Owner · Lead status · GPP stage · Refs · Last
            contact) plus the Atlas record dates. overflowY stays 'hidden'
            (not 'auto'): WindowedRows walks up to the nearest overflow-y
            auto/scroll ancestor for its scroll events, and that must resolve
            to the page scroller above, not this card. */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                       borderRadius: 8, overflowX: 'auto', overflowY: 'hidden' }}>
          {partners === null && (
            <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
          )}
          {empty && (
            <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)' }}>
              No partners match the current filters. Adjust the filters above, or run{' '}
              <code className="mono" style={{ fontSize: 11, padding: '1px 4px', background: 'var(--surface-2)', borderRadius: 3 }}>
                python scripts/migrate_gpp_partners.py
              </code>{' '}
              to re-import from DealCloud.
            </div>
          )}
          {partners && filtered.length > 0 && (
            <div style={{ minWidth: 'max-content' }}>
              <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)',
                            fontSize: 11, color: 'var(--text-3)', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                            borderBottom: '1px solid var(--border)' }}>
                {columns.cols.map((c) => (
                  <window.SortableHeader key={c.key} col={c}
                    sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                    sortable={Boolean(GPP_SORT_EXTRACT[c.key])}
                    onResizeStart={columns.startResize(c.key)}
                  />
                ))}
              </div>
              <window.WindowedRows rows={sorted} renderRow={(p) => (
                <GppRow key={p.id} p={p} cols={columns.cols} cellCtx={cellCtx}
                  onOpen={setContactId} />
              )} />
            </div>
          )}
        </div>
      </div>
      )}

      {addingContact && (
        <NewPersonDrawer
          initialType="gpp"
          onClose={() => setAddingContact(false)}
          onCreated={(contact) => {
            setAddingContact(false);
            // Chain straight into the promote form — a partner isn't real
            // until it has an owner and a partner type.
            setPromoteContact(contact);
          }}
        />
      )}
      {promoteContact && (
        <window.GppPartnerEditModal
          open={true}
          mode="promote"
          partner={null}
          contact={{ _raw: promoteContact }}
          users={users}
          onClose={() => setPromoteContact(null)}
          onSaved={() => { setPromoteContact(null); reload(); }}
        />
      )}
    </div>
  );
};

// ============ PEOPLE ============
// Same SORT_EXTRACT pattern as deals/companies. Company name comes from the
// joined company_name on the contact row; falls back to empty string so blank
// rows sink during sort.
// Flat export shape for CSV/JSON — the grid's columns plus the Atlas id,
// raw values so Excel and scripts can compute (see deals.jsx for the pattern).
// Identifier columns appended to every people export regardless of layout,
// so a file can always be joined back to Atlas. Everything else comes from
// the visible columns — see window.exportColumnsFor.
const PEOPLE_EXPORT_ALWAYS = [
  { key: 'id', label: 'Atlas id', get: (p) => p.id },
];

// Sort keys the server can order by — see _CONTACT_SORTABLE in
// routes/contacts.py. `company` and `contactOwner` are lookup names on other
// tables, and `roles` is a derived array, so those three stay client-side
// over the loaded pages (the Sort chip says which you got).
const PEOPLE_SERVER_SORT = new Set([
  'name', 'title', 'email', 'phone', 'city', 'state', 'country', 'linkedin',
  'dcContactType', 'decisionMaker', 'interactions', 'lastInteraction',
  'webVisits', 'lastWebVisit', 'referralDate', 'created', 'updated',
]);

const PEOPLE_SORT_EXTRACT = {
  name:    (p) => (p.name || '').toLowerCase(),
  title:   (p) => (p.title || '').toLowerCase(),
  company: (p) => (p.company || '').toLowerCase(),
  email:   (p) => (p.email || '').toLowerCase(),
  phone:   (p) => (p.phone || '').toLowerCase(),
  interactions: (p) => p.interactions || 0,
  webVisits:    (p) => p.webVisits || 0,
  // DealCloud's contact record dates — see data.jsx on why Atlas's own
  // created_at/updated_at describe the sync rather than the contact.
  ...window.recordDateSort(),
  // Opt-in columns (PEOPLE_COLUMNS, def:false) — client-side over loaded pages.
  lastInteraction: (p) => p.lastInteractionAt || '',
  lastWebVisit:    (p) => p.lastWebVisitAt || '',
  city:            (p) => ((p._raw || {}).city || '').toLowerCase(),
  state:           (p) => ((p._raw || {}).state || '').toLowerCase(),
  country:         (p) => ((p._raw || {}).country || '').toLowerCase(),
  linkedin:        (p) => ((p._raw || {}).linkedin_url || '').toLowerCase(),
  contactOwner:    (p) => ((p._raw || {}).contact_owner_name || '').toLowerCase(),
  dcContactType:   (p) => ((p._raw || {}).dc_contact_type || '').toLowerCase(),
  decisionMaker:   (p) => ((p._raw || {}).is_decision_maker ? 0 : 1),   // Yes first
  referralDate:    (p) => (p._raw || {}).referral_date || '',
  referralSource:  (p) => (((p._raw || {}).referral_source_user_name || (p._raw || {}).referral_source_contact_name) || '').toLowerCase(),
};

// Top-level directory of all contacts. Self-fetches with pagination; doesn't
// rely on an App-level bulk array.
const contactDcLocked = (p, field) => (p._raw?.dc_synced_fields || []).includes(field);

// ────────────────────────────────────────────────────────────────────────
// The people column catalog — one definition per column (label, width
// sample, cell), mapped by BOTH the header and the row. `def: false` ships
// hidden but offerable in the Columns menu. render(p, ctx, col) must size its
// outer element to col.w.
const PEOPLE_COLUMNS = [
  {
    key: 'name', label: 'Name', accessor: (p) => p.name, extra: 30, fixed: true,
    xget: (p) => (p.name === '—' ? '' : (p.name || '')),
    render: (p, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8, borderRight: '1px solid var(--border)' }}>
        <Avatar name={p.name} size={20} color="oklch(0.50 0.13 295)" />
        <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{p.name}</span>
      </div>
    ),
  },
  {
    key: 'title', label: 'Title', accessor: (p) => p.title,
    xget: (p) => (p.title === '—' ? '' : (p.title || '')),
    render: (p, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', fontSize: 12, color: 'var(--text-2)', borderRight: '1px solid var(--border)' }}>
        <window.EditableCell type="text" fill value={p.title || ''} locked={contactDcLocked(p, 'title')} onSave={(title) => ctx.onSave(p.id, { title })} />
      </div>
    ),
  },
  {
    key: 'company', label: 'Company', accessor: (p) => p.company,
    xget: (p) => (p.company === '—' ? '' : (p.company || '')),
    render: (p, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', fontSize: 12, color: 'var(--text-2)', borderRight: '1px solid var(--border)', display: 'flex', alignItems: 'center' }}>{p.company || '—'}</div>
    ),
  },
  {
    key: 'email', label: 'Email', accessor: (p) => p.email,
    xget: (p) => p.email || '',
    render: (p, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', fontSize: 12, color: 'var(--accent-text)', borderRight: '1px solid var(--border)' }}>
        <window.EditableCell type="text" fill value={p.email || ''} locked={contactDcLocked(p, 'email')} displayAs={bgEmailView(p.email)} onSave={(email) => ctx.onSave(p.id, { email })} />
      </div>
    ),
  },
  {
    key: 'phone', label: 'Phone', accessor: (p) => p.phone,
    xget: (p) => p.phone || '',
    render: (p, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ ...window.EDIT_CELL_WRAP, width: col.w, padding: '0 10px', fontSize: 12, color: 'var(--text-3)', borderRight: '1px solid var(--border)' }}>
        <window.EditableCell type="text" fill value={p.phone || ''} locked={contactDcLocked(p, 'phone')} displayAs={bgPhoneView(p.phone)} onSave={(phone) => ctx.onSave(p.id, { phone })} />
      </div>
    ),
  },
  {
    // Derived roles — read-only chips (recomputed hourly, not editable), and
    // deliberately unsortable: PEOPLE_SORT_EXTRACT has no `roles` key.
    key: 'roles', label: 'Roles', accessor: (p) => (p.roles || []).join(' '), extra: 40,
    xget: (p) => (p.roles || []).join('; '),
    render: (p, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', overflow: 'hidden', borderRight: '1px solid var(--border)' }}>
        <window.RoleChips roles={p.roles} max={2} />
      </div>
    ),
  },
  {
    // Derived interaction tally — read-only, recomputed hourly.
    key: 'interactions', label: 'Interactions', align: 'right', accessor: (p) => String(p.interactions || 0),
    xget: (p) => p.interactions ?? 0,
    xextra: [{ key: 'last_interaction', label: 'Last interaction', get: (p) => p.lastInteractionAt || '' }],
    render: (p, ctx, col) => (
      <div key={col.key} title={p.lastInteractionAt ? `Last interaction: ${window.fmtDate(p.lastInteractionAt)}` : ''}
        style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 12, color: p.interactions ? 'var(--text-2)' : 'var(--text-4)', borderRight: '1px solid var(--border)' }} className="num">
        {p.interactions || 0}
      </div>
    ),
  },
  {
    // Website visits (RB2B + the site's snippet; app/web_rollups.py) — read-only.
    key: 'webVisits', label: 'Website visits', align: 'right', accessor: (p) => String(p.webVisits || 0),
    xget: (p) => p.webVisits ?? 0,
    xextra: [{ key: 'last_web_visit', label: 'Last website visit', get: (p) => p.lastWebVisitAt || '' }],
    render: (r, ctx, col) => (
      <div key={col.key} title={r.lastWebVisitAt ? `Last website visit: ${window.fmtDate(r.lastWebVisitAt)}` : ''}
        style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 12, color: r.webVisits ? 'var(--text-2)' : 'var(--text-4)', borderRight: '1px solid var(--border)' }} className="num">
        {r.webVisits || 0}
      </div>
    ),
  },
  {
    key: 'created', label: 'Created', accessor: (p) => window.fmtDate(window.recordCreated(p._raw)),
    xget: (p) => window.recordCreated(p._raw) || '',
    render: (p, ctx, col) => <window.RecordDateCell key={col.key} row={p._raw} kind="created" w={col.w} border />,
  },
  {
    key: 'updated', label: 'Updated', accessor: (p) => window.fmtDate(window.recordModified(p._raw)),
    xget: (p) => window.recordModified(p._raw) || '',
    render: (p, ctx, col) => <window.RecordDateCell key={col.key} row={p._raw} kind="modified" w={col.w} border last={col.last} />,
  },

  // ── Off by default, one click away in the Columns menu.
  ...[
    { key: 'lastInteraction', label: 'Last interaction', group: 'Activity', date: (p) => p.lastInteractionAt },
    { key: 'lastWebVisit', label: 'Last website visit', group: 'Activity', date: (p) => p.lastWebVisitAt },
    { key: 'recordId',    label: 'ID',          group: 'Contact details', text: (p) => p.dealcloudId,
      desc: 'The person’s ID — the number on their page, in #/id/<n> links and in imports.' },
    { key: 'city',        label: 'City',        group: 'Location', text: (p) => (p._raw || {}).city },
    { key: 'state',       label: 'State',       group: 'Location', text: (p) => (p._raw || {}).state },
    { key: 'country',     label: 'Country',     group: 'Location', text: (p) => (p._raw || {}).country },
    { key: 'linkedin',    label: 'LinkedIn',    group: 'Contact details', text: (p) => (p._raw || {}).linkedin_url },
    { key: 'contactOwner', label: 'Contact owner', group: 'Ownership', text: (p) => (p._raw || {}).contact_owner_name },
    { key: 'dcContactType', label: 'DC contact type', group: 'Classification', text: (p) => (p._raw || {}).dc_contact_type,
      desc: "DealCloud's human-maintained Contact Type — the bulk 'Seller: Primary Contact' default is hidden elsewhere." },
    { key: 'decisionMaker', label: 'Decision maker', group: 'Classification', text: (p) => ((p._raw || {}).is_decision_maker ? 'Yes' : '') },
    { key: 'referralDate', label: 'Referral date', group: 'Referral', date: (p) => (p._raw || {}).referral_date },
    { key: 'referralSource', label: 'Referral source', group: 'Referral',
      text: (p) => (p._raw || {}).referral_source_user_name || (p._raw || {}).referral_source_contact_name },
  ].map((c) => ({
    key: c.key, label: c.label, group: c.group, desc: c.desc, align: c.align, def: false,
    accessor: (p) => (c.date ? (window.fmtDate(c.date(p)) || '') : (c.text(p) || '')),
    // Export the underlying value (ISO dates), not the rendered one.
    xget: (p) => (c.date ? (c.date(p) || '') : (c.text(p) || '')),
    render: (p, ctx, col) => {
      const v = c.date ? window.fmtDate(c.date(p)) : c.text(p);
      return (
        <div key={col.key} style={{
          width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center',
          justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
          fontSize: 11.5, color: v ? 'var(--text-2)' : 'var(--text-4)',
          borderRight: col.last ? 'none' : '1px solid var(--border)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} className={c.align === 'right' ? 'num' : ''}>
          {v || '—'}
        </div>
      );
    },
  })),
];

// Memoized row — an inline cell edit swaps ONE row object in items, so only
// that row re-renders instead of every loaded row.
const PersonRow = React.memo(({ p, cols, cellCtx, onOpen, checked = false, onToggle }) => {
  return (
    <div
      onClick={() => onOpen && onOpen(p.id)}
      style={{
        display: 'flex', minHeight: 38, alignItems: 'center',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)',
        cursor: onOpen ? 'pointer' : 'default',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
    >
      <div style={{ width: 36, padding: '0 10px' }} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={checked} onChange={() => onToggle && onToggle(p.id)}
          style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
      </div>
      {cols.map((col) => col.render(p, cellCtx, col))}
    </div>
  );
});

const PeopleView = ({ setView, setContactId }) => {
  const [items, setItems] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [q, setQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [importing, setImporting] = React.useState(false);  // bulk update from a file
  // Checked rows → "Edit N" (window.BulkEditDrawer). Cleared on every reload
  // so a selection never outlives the list it was made on.
  const [selected, setSelected] = React.useState(() => new Set());
  const [bulkEditing, setBulkEditing] = React.useState(false);
  const toggleRow = React.useCallback((id) => {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  // Sort + filter — default = no sort, server returns by created_at desc.
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState('asc');
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [filters, setFilters] = React.useState({
    has_email: '',  // '' / 'yes' / 'no'
    has_phone: '',
    company:   '',  // company_id
    role:      '',  // derived role (server-side, contacts.roles @> …)
    // Referred-only is server-side (referred_only=true). Lives in the same
    // filters object so the chip count is consistent with the others.
    referred_only: false,
  });
  // The picked company option ({value, label}) — FilterPopover unmounts on
  // close, so the label has to live up here for the trigger to show the
  // company's name (not "Any") when the popover reopens.
  const [companyPick, setCompanyPick] = React.useState(null);
  // Cursor pagination — same pattern as deals/companies/notes/referrals.
  // Server-side filters (debouncedQ, referred_only) reset the cursor via
  // reload(). Client-side filters (has_email/has_phone/company) just trim
  // the already-loaded list — they don't refetch.
  const [page, setPage] = React.useState(1);
  const [paging, setPaging] = React.useState(false);
  const [loadedAll, setLoadedAll] = React.useState(false);
  const sentinelRef = React.useRef(null);
  const PAGE_SIZE = 120;

  const toggleSort = React.useMemo(
    () => window.makeToggleSort(setSortKey, setSortDir),
    [setSortKey, setSortDir]
  );

  // Inline cell editing — PATCH the contact, swap the freshly-adapted row into
  // state (optimistic); EditableCell reverts its draft if the PATCH throws.
  // Only the edited row object changes identity, so the memoized PersonRow
  // re-renders alone.
  const saveContact = React.useCallback(async (id, patch) => {
    const updated = await window.AtlasApi.updateContact(id, patch);
    setItems((prev) => (prev || []).map((x) => (x.id === id ? window.AtlasData.__adaptContact(updated) : x)));
  }, []);
  // Per-user column layout (105).
  const columns = window.useColumnPrefs('people', PEOPLE_COLUMNS, {
    rows: items || [], fitMax: 360,
  });
  const startResize = columns.startResize;
  // One object so the memoized row can't capture a stale per-cell closure.
  const cellCtx = React.useMemo(() => ({ onSave: saveContact }), [saveContact]);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const buildParams = React.useCallback(() => {
    const params = { my_team: true };
    if (debouncedQ) params.q = debouncedQ;
    // Server-side referral filter — much faster than pulling 10K contacts
    // and filtering client-side (and matches the dedicated Referrals view's
    // sort order: most-recently-referred first).
    if (filters.referred_only) params.referred_only = true;
    // Presence + company filters are server-side too: they must apply to the
    // whole 318k-contact scope, not just the pages we happened to load.
    if (filters.has_email) params.has_email = filters.has_email === 'yes';
    if (filters.has_phone) params.has_phone = filters.has_phone === 'yes';
    if (filters.company) params.company_id = filters.company;
    if (filters.role) params.role = filters.role;
    // Column sort — server-side where the key maps to a real contacts column,
    // so it ranks all 318k rows rather than the pages already loaded.
    if (sortKey && PEOPLE_SERVER_SORT.has(sortKey)) {
      params.sort = sortKey;
      params.sort_dir = sortDir;
    }
    return params;
  }, [debouncedQ, filters, sortKey, sortDir]);

  // Stale-response guard — a slow response from an older query/filter combo
  // must not overwrite fresher rows.
  const reqSeq = React.useRef(0);

  const fetchPage = React.useCallback(async (p, append) => {
    const seq = ++reqSeq.current;
    setPaging(true);
    try {
      const res = await window.AtlasApi.listContacts({
        ...buildParams(), page: p, limit: PAGE_SIZE,
      });
      if (seq !== reqSeq.current) return;   // superseded — drop silently
      const rows = ((res && res.items) || []).map(window.AtlasData.__adaptContact);
      const newTotal = res?.total ?? rows.length;
      setItems((prev) => {
        const next = append && prev ? [...prev, ...rows] : rows;
        if (rows.length === 0 || next.length >= newTotal) setLoadedAll(true);
        return next;
      });
      setTotal(newTotal);
      setPage(p);
    } catch {
      if (seq !== reqSeq.current) return;
      if (!append) { setItems([]); setTotal(0); setLoadedAll(true); }
    } finally {
      if (seq === reqSeq.current) setPaging(false);
    }
  }, [buildParams]);

  const reload = React.useCallback(() => {
    setItems(null);
    setPage(1);
    setLoadedAll(false);
    setSelected(new Set());
    return fetchPage(1, false);
  }, [fetchPage]);

  React.useEffect(() => { reload(); }, [reload]);

  const hasMore = items != null && !loadedAll && items.length < total;
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !paging) {
        fetchPage(page + 1, true);
      }
    }, { rootMargin: '240px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, paging, page, fetchPage]);

  // Filters and most sorts are server-side now (buildParams). Only the keys
  // outside PEOPLE_SERVER_SORT still sort here, over the loaded pages —
  // re-sorting a server-ordered result would silently undo it, re-ranking the
  // loaded window and presenting that as the whole 318k.
  const list = React.useMemo(() => {
    let r = items || [];
    if (sortKey && !PEOPLE_SERVER_SORT.has(sortKey)) {
      r = [...r].sort(window.makeComparator(sortKey, sortDir, PEOPLE_SORT_EXTRACT));
    }
    return r;
  }, [items, sortKey, sortDir]);

  // Whole-view export (no row selection on this grid). Refetches in one call
  // when the pager hasn't finished; 318K contacts means the server's 10K cap
  // can truncate — the menu says so rather than pretend the file is complete.
  const EXPORT_CAP = 10000;
  const doExport = React.useCallback(async (format) => {
    let data;
    if (loadedAll) {
      data = list;
    } else {
      const res = await window.AtlasApi.listContacts({ ...buildParams(), page: 1, limit: EXPORT_CAP });
      data = ((res && res.items) || []).map(window.AtlasData.__adaptContact);
      if (sortKey && !PEOPLE_SERVER_SORT.has(sortKey)) {
        data = [...data].sort(window.makeComparator(sortKey, sortDir, PEOPLE_SORT_EXTRACT));
      }
    }
    // The file mirrors the grid: the columns you can see, in your order.
    const xcols = window.exportColumnsFor(columns.cols, PEOPLE_EXPORT_ALWAYS);
    const records = window.exportRecords(xcols, data);
    const name = `people-${window.localISODate()}`;
    if (format === 'csv') window.exportCsv(`${name}.csv`, xcols, records);
    else window.exportJson(`${name}.json`, records);
  }, [loadedAll, list, buildParams, sortKey, sortDir, columns.cols]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <BgPageHeader
        breadcrumb={['Workspace', 'People']}
        title="People"
        meta={items === null ? 'Loading…' : `${list.length.toLocaleString()} of ${total.toLocaleString()} contacts`}
        actions={(
          <>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people…"
              style={{
                width: 220, padding: '4px 10px', fontSize: 12,
                border: '1px solid var(--border)', borderRadius: 6, height: 28, fontFamily: 'inherit',
              }}
            />
            <Btn size="sm" variant="outline" icon={<Icon.Upload size={12} />} onClick={() => setImporting(true)}
              title="Update people from a CSV or Excel file — preview before anything changes">Import</Btn>
            {selected.size > 0 && (
              <Btn size="sm" variant="outline" icon={<Icon.Edit size={12} />} onClick={() => setBulkEditing(true)}
                title="Set one field on every checked person">
                Edit {selected.size}
              </Btn>
            )}
            <window.ExportMenu
              disabled={items === null || !list.length}
              viewCount={loadedAll ? list.length : total}
              capNote={!loadedAll && total > EXPORT_CAP
                ? `Caps at the first ${EXPORT_CAP.toLocaleString()} rows — narrow the search/filters for a complete file.`
                : null}
              onExport={doExport}
            />
            <window.ColumnsMenu
              catalog={PEOPLE_COLUMNS}
              cols={columns.cols}
              toggle={columns.toggle}
              move={columns.move}
              reset={columns.reset}
            />
            <div style={{ position: 'relative' }}>
              <Btn size="sm" variant={window.activeFilterCount(filters) ? 'accent' : 'outline'}
                icon={<Icon.Filter size={12} />}
                onClick={() => setFilterOpen((v) => !v)}>
                Filter
                {window.activeFilterCount(filters) > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 10.5, opacity: 0.85 }}>
                    {window.activeFilterCount(filters)}
                  </span>
                )}
              </Btn>
              {filterOpen && (
                <window.FilterPopover
                  filters={filters}
                  setFilters={setFilters}
                  onClose={() => setFilterOpen(false)}
                  spec={[
                    { key: 'referred_only', label: 'Referred contacts only',
                      type: 'toggle',
                      hint: 'Hide cold contacts — show only those with an incoming referral on file.' },
                    // Server-side derived-role filter (contacts.roles, GIN).
                    { key: 'role', label: 'Role', type: 'select',
                      placeholder: 'Any role',
                      options: Object.entries(window.ROLE_META).map(([value, m]) => ({ value, label: m.label })) },
                    { key: 'has_email', label: 'Has email', type: 'select',
                      placeholder: 'Any', options: [
                        { value: 'yes', label: 'Has email' },
                        { value: 'no',  label: 'Missing email' },
                      ] },
                    { key: 'has_phone', label: 'Has phone', type: 'select',
                      placeholder: 'Any', options: [
                        { value: 'yes', label: 'Has phone' },
                        { value: 'no',  label: 'Missing phone' },
                      ] },
                    // Server-searchable — a preloaded slice (the old my_team
                    // limit:500) hid most of the 312k-company mirror, so
                    // filtering on an unlisted company was impossible.
                    { key: 'company', label: 'Company', type: 'select',
                      placeholder: 'Any',
                      loadOptions: window.searchCompanyOptions,
                      options: companyPick ? [companyPick] : [],
                      onPick: setCompanyPick },
                  ]}
                />
              )}
            </div>
            {/* Says which scope the active sort has. Company, Contact owner
                and Roles can only rank the pages already loaded, so on a
                318k-row table that is a materially different answer from the
                server-side sorts — the chip shouldn't let the two look alike. */}
            <Btn size="sm" variant="outline" icon={<Icon.Sort size={12} />}
              onClick={() => { setSortKey(null); setSortDir('asc'); }}
              title={!sortKey
                ? 'Click a column header to sort. Default is alphabetical by last name.'
                : PEOPLE_SERVER_SORT.has(sortKey)
                  ? `Sorted by ${sortKey} across all ${total.toLocaleString()} contacts. Click to reset to alphabetical.`
                  : `Sorted by ${sortKey} over the ${(items || []).length.toLocaleString()} rows loaded so far — this column can't be ordered on the server. Click to reset to alphabetical.`}>
              {sortKey ? `Sort: ${sortKey} ${sortDir === 'desc' ? '↓' : '↑'}` : 'Sort'}
              {sortKey && !PEOPLE_SERVER_SORT.has(sortKey) && (
                <span style={{ marginLeft: 5, fontSize: 10, color: 'var(--amber-fg)' }}>loaded rows</span>
              )}
            </Btn>
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />}
              onClick={() => setCreating(true)}>New person</Btn>
          </>
        )}
      />
      <div style={{ flex: 1, overflow: 'auto' }}>
        {items === null && (
          <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
        )}
        {items && list.length === 0 && (
          <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>No contacts match.</div>
        )}
        {items && list.length > 0 && (
          <div style={{ minWidth: 'max-content' }}>
            <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)', position: 'sticky', top: 0, zIndex: 1, fontSize: 11, color: 'var(--text-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 36, padding: '0 10px' }}>
                <input type="checkbox" style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                  checked={list.length > 0 && list.every((p) => selected.has(p.id))}
                  onChange={() => setSelected((prev) => (list.every((p) => prev.has(p.id)) ? new Set() : new Set(list.map((p) => p.id))))} />
              </div>
              {columns.cols.map((c) => (
                <window.SortableHeader key={c.key} col={c}
                  sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                  sortable={Boolean(PEOPLE_SORT_EXTRACT[c.key])}
                  onResizeStart={startResize(c.key)}
                />
              ))}
            </div>
            <window.WindowedRows rows={list} renderRow={(p) => (
              <PersonRow key={p.id} p={p} cols={columns.cols} cellCtx={cellCtx}
                onOpen={setContactId} checked={selected.has(p.id)} onToggle={toggleRow} />
            )} />
            {/* Sentinel for IntersectionObserver-driven pagination. */}
            {hasMore && items !== null && (
              <div ref={sentinelRef} style={{ height: 1 }} />
            )}
            {paging && items !== null && items.length > 0 && (
              <SkeletonRows rows={3} variant="generic" />
            )}
            {!hasMore && items !== null && items.length > 0 && (
              <div style={{
                padding: '12px 14px', textAlign: 'center', fontSize: 11.5,
                color: 'var(--text-4)',
              }}>
                End of list · {items.length.toLocaleString()} {items.length === 1 ? 'contact' : 'contacts'}
              </div>
            )}
          </div>
        )}
      </div>

      {window.ImportDrawer && (
        <window.ImportDrawer kind="contacts" open={importing} onClose={() => setImporting(false)}
          onDone={() => { reload(); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }} />
      )}

      {window.BulkEditDrawer && (
        <window.BulkEditDrawer open={bulkEditing} kind="people" ids={[...selected]}
          onClose={() => setBulkEditing(false)}
          onDone={() => { reload(); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }} />
      )}

      {creating && (
        <NewPersonDrawer
          onClose={() => setCreating(false)}
          onCreated={(contact) => {
            setCreating(false);
            reload();
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
            if (contact?.id && setContactId) setContactId(contact.id);
          }}
        />
      )}
    </div>
  );
};

// `initialCompany` ({id, name}) pre-links a company — used by the quick-add
// button on the company detail Contacts panel; it's prepended as a static
// option so the picker shows it without a search round-trip.
// `initialName` seeds first/last from a search query ("Stephen Weitzel") so
// pickers can offer "create this person" without retyping. If the company
// doesn't exist either, "+ New company" creates it in place — no detour to
// the Companies page (the create-cascade complaint from the 7/22 walkthrough).
const NewPersonDrawer = ({ onClose, onCreated, initialCompany, initialType, initialName, zIndex }) => {
  const seedName = (initialName || '').trim().split(/\s+/);
  const [firstName, setFirstName] = React.useState(seedName[0] || '');
  const [lastName, setLastName]   = React.useState(seedName.slice(1).join(' ') || '');
  const [title, setTitle]         = React.useState('');
  const [email, setEmail]         = React.useState('');
  const [phone, setPhone]         = React.useState('');
  const [companyId, setCompanyId] = React.useState(initialCompany?.id || '');
  const [type, setType]           = React.useState(initialType || 'seller');
  const [ownerId, setOwnerId]     = React.useState('');
  const [linkedin, setLinkedin]   = React.useState('');
  // Static picker extras only (preset company, just-created company) — the
  // Combobox searches the server for everything else, so companies outside
  // any pre-loaded slice stay findable.
  const [companies, setCompanies] = React.useState(
    initialCompany?.id ? [{ value: initialCompany.id, label: initialCompany.name }] : []
  );
  const [members, setMembers]     = React.useState([]);
  const [busy, setBusy]           = React.useState(false);
  const [creatingCompany, setCreatingCompany] = React.useState(false);
  const [industries, setIndustries] = React.useState(null);  // lazy — only if "+ New company" opens
  // Create-time duplicate guard: as name/email/company are typed, ask the
  // server for likely-existing contacts (email match, or same name at the
  // same company). Advisory — "Use this" hands the existing contact to
  // onCreated without creating anything.
  const [dupes, setDupes]         = React.useState([]);

  React.useEffect(() => {
    const fn = firstName.trim(), ln = lastName.trim(), em = email.trim();
    if ((fn + ln).length < 3 && !em.includes('@')) { setDupes([]); return; }
    let stale = false;
    const t = setTimeout(() => {
      window.AtlasApi.checkContactDuplicates({
        first_name: fn || undefined, last_name: ln || undefined,
        email: em || undefined, company_id: companyId || undefined,
      })
        .then((r) => { if (!stale) setDupes((r && r.matches) || []); })
        .catch(() => { if (!stale) setDupes([]); });
    }, 350);
    return () => { stale = true; clearTimeout(t); };
  }, [firstName, lastName, email, companyId]);

  const DUPE_REASON = {
    email:           'same email',
    name_at_company: 'same name at this company',
    name_exact:      'same name',
  };

  const openCompanyCreate = () => {
    if (industries === null) {
      window.AtlasApi.listIndustries()
        .then((i) => setIndustries(Array.isArray(i) ? i : []))
        .catch(() => setIndustries([]));
    }
    setCreatingCompany(true);
  };

  React.useEffect(() => {
    window.AtlasData.loadTeamMembers()
      .then((m) => setMembers(Array.isArray(m) ? m : []))
      .catch(() => setMembers([]));
  }, []);

  const canSubmit = firstName.trim() && lastName.trim() && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const contact = await window.AtlasApi.createContact({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        title: title.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        company_id: companyId || null,
        contact_type: type,
        contact_owner_id: ownerId || null,
        linkedin_url: linkedin.trim() || null,
      });
      pushToast({ type: 'success', title: 'Contact created', body: `${firstName.trim()} ${lastName.trim()}` });
      onCreated(contact);
    } catch (e) {
      pushToast({ type: 'error', title: 'Create failed', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      zIndex={zIndex}
      widthKey="newPerson"
      title="New person"
      subtitle="First and last name are required."
      footer={(
        <>
          <Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={submit} disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create contact'}
          </Btn>
        </>
      )}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Field label="First name">
            {/* No autoFocus on touch: the keyboard would open over the sheet on mount. */}
            <TextInput value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus={!bgTouch()} />
          </Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="Last name">
            <TextInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
        </div>
      </div>
      <Field label="Title"><TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Owner / CFO / …" /></Field>
      {/* inputMode / autoCapitalize only shape the on-screen keyboard —
          desktop browsers ignore them. */}
      <Field label="Email"><TextInput value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" inputMode="email" autoCapitalize="off" /></Field>
      {dupes.length > 0 && (
        <div style={{
          border: '1px solid var(--amber-border, var(--border-strong))', borderRadius: 7,
          background: 'var(--amber-soft, var(--surface-2))', padding: '8px 10px', marginBottom: 12,
        }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
            May already exist in Atlas — link instead of creating a duplicate:
          </div>
          {dupes.map((d) => {
            // A contact has exactly ONE company (company_id = primary
            // employer, mirrored to DealCloud) — so "using" an existing
            // contact from another company MOVES them here, and must
            // actually PATCH or the link evaporates on refresh.
            const name = `${d.first_name || ''} ${d.last_name || ''}`.trim();
            const moves = !!(companyId && d.company_id && d.company_id !== companyId);
            const relinks = !!(companyId && d.company_id !== companyId);
            const useExisting = async () => {
              let picked = d;
              if (relinks) {
                setBusy(true);
                try {
                  picked = await window.AtlasApi.updateContact(d.id, { company_id: companyId });
                } catch (e) {
                  pushToast({ type: 'error', title: 'Link failed', body: e.message });
                  setBusy(false);
                  return;
                }
                setBusy(false);
              }
              pushToast({
                type: 'success',
                title: moves ? 'Contact moved' : 'Linked existing contact',
                body: moves && d.company_name ? `${name} — moved from ${d.company_name}` : name,
              });
              onCreated(picked);
            };
            // Two-businesses case (an owner who wears hats at two companies):
            // clone the existing contact into a second record at THIS company,
            // leaving the original untouched. Typed drawer fields win over the
            // source record so the second hat can carry its own title/phone.
            const addCopy = async () => {
              setBusy(true);
              try {
                let src = d;
                try { src = await window.AtlasApi.getContact(d.id); } catch { /* match fields are enough */ }
                const created = await window.AtlasApi.createContact({
                  first_name: src.first_name || firstName.trim() || '?',
                  last_name: src.last_name || lastName.trim() || '?',
                  title: title.trim() || src.title || null,
                  email: email.trim() || src.email || null,
                  phone: phone.trim() || src.phone || null,
                  city: src.city || null,
                  state: src.state || null,
                  company_id: companyId,
                  contact_type: type,
                  contact_owner_id: ownerId || src.contact_owner_id || null,
                  linkedin_url: linkedin.trim() || src.linkedin_url || null,
                });
                pushToast({
                  type: 'success', title: 'Second contact record created',
                  body: d.company_name ? `${name} — still at ${d.company_name} too` : name,
                });
                onCreated(created);
              } catch (e) {
                pushToast({ type: 'error', title: 'Create failed', body: e.message });
              }
              setBusy(false);
            };
            return (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span style={{ fontWeight: 600 }}>{name}</span>
                <span style={{ color: 'var(--text-4)', marginLeft: 6 }}>
                  {[d.email, d.company_name].filter(Boolean).join(' · ')}
                </span>
              </span>
              <Pill hue="slate">{DUPE_REASON[d.reason] || d.reason}</Pill>
              {moves && (
                <Btn size="xs" variant="outline" disabled={busy}
                  title={`Creates a second contact record at this company and keeps the one at ${d.company_name || 'their current company'} — for people at two businesses.`}
                  onClick={addCopy}>
                  Add copy
                </Btn>
              )}
              <Btn size="xs" variant="outline" disabled={busy}
                title={moves
                  ? `A contact belongs to one company — this moves ${name} out of ${d.company_name || 'their current company'}. To keep them at both, use “Add copy”.`
                  : undefined}
                onClick={useExisting}>
                {moves ? 'Move here' : 'Use this'}
              </Btn>
            </div>
            );
          })}
        </div>
      )}
      <Field label="Phone"><TextInput value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" /></Field>
      <Field label="Company" hint="Optional — search by name, or create it right here.">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Combobox value={companyId} onChange={(e) => setCompanyId(e.target.value)}
              placeholder="Search companies…"
              options={[{ value: '', label: '(none)' }, ...companies]}
              loadOptions={window.searchCompanyOptions} />
          </div>
          <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
            onClick={openCompanyCreate} title="Create a company that isn't in Atlas yet">
            New company
          </Btn>
        </div>
      </Field>
      <Field label="Type" hint="Professional services = wealth advisors, accountants, insurance, M&A attorneys.">
        <Select value={type} onChange={(e) => setType(e.target.value)}
          options={[
            { value: 'seller',       label: 'Seller' },
            { value: 'buyer',        label: 'Buyer' },
            { value: 'gpp',          label: 'GPP partner' },
            { value: 'professional', label: 'Professional services' },
            { value: 'internal',     label: 'Internal' },
          ]} />
      </Field>
      <Field label="Owner" hint="Optional — the dealmaker who owns this relationship.">
        <Select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
          options={[
            { value: '', label: '(unassigned)' },
            ...members.filter(window.isOwnerEligible)
              .map((m) => ({ value: m.id, label: `${m.full_name} (${(m.role || '').replace(/_/g, ' ')})` })),
          ]} />
      </Field>
      <Field label="LinkedIn URL"><TextInput value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/…" /></Field>
      {/* Portaled to body so the stacked drawer isn't nested inside this
          drawer's <Field> label (label click-forwarding + focus traps). */}
      {creatingCompany && ReactDOM.createPortal(
        <window.NewCompanyDrawer
          industries={industries || []}
          members={members}
          zIndex={zIndex}
          onClose={() => setCreatingCompany(false)}
          onCreated={(company) => {
            setCreatingCompany(false);
            if (company?.id) {
              setCompanies((prev) => [{ value: company.id, label: company.name }, ...prev]);
              setCompanyId(company.id);
            }
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />, document.body)}
    </Drawer>
  );
};

window.BuyersView = BuyersView;
// Shared with the buyer page (views/buyer_detail.jsx): the drawer bodies and
// the roster's labels/formatters, so both render a firm the same way.
window.BuyerPassesBody = BuyerPassesBody;
window.BuyerTeamBody = BuyerTeamBody;
window.BuyerPortfolioBody = BuyerPortfolioBody;
window.BuyerLabels = { BUYER_TYPE_LABEL, BUYER_TYPE_HUE, FIRM_KIND_LABEL, FIRM_KIND_HUE, AGREEMENT_LABEL, ROLE_BUCKET_LABEL,
                       fmtBuyerAgo, fmtBuyerDate, fmtBuyerBand, fmtBuyerMoney, buyerDaysToExpiry, buyerExpiryHue };
window.BuyerSearchDrawer = BuyerSearchDrawer;
window.BUYER_SEARCH_STEP_LABEL = SEARCH_STEP_LABEL;
window.GppView = GppView;
window.PeopleView = PeopleView;
window.NewPersonDrawer = NewPersonDrawer;
